
import * as THREE from "../../../libs/three.js/build/three.module.js";
import {
	orthonormalBasis,
	distanceToAxis,
	paramAlongAxis,
	refineCylinderIRLS,
	robustWeight,
} from "./cylinderFit.js";
import {computeSurfaceSupport, computeSurfaceSupportXY, buildSurfaceClassification, surfaceInlierWeight, isSurfaceInlier, longitudinalContinuityFilter, maskCoverageDeg} from "./surfaceSupport.js";
import {
	APPEARANCE_DEFAULTS, buildAppearanceModel, updateAppearanceModel, appearancePointSimilarity,
	appearanceSetSimilarity, appearanceMismatchFraction, appearanceModelConfidence, appearanceContribution,
} from "./appearanceModel.js";

import {t} from "../i18n/index.js";
// ─────────────────────────────────────────────────────────────────────────
// Semi-automatic pipe/utility-line TRACKER — isolated mathematical core.
// Pure geometry (THREE.Vector3), NO scene / DOM / octree / worker. The one
// interface to the point cloud is an injected
//     queryPoints(center, tangent, radialExtent, axialHalfLength) -> Vector3[]
// closure returning the points inside an oriented cylinder ROI.
//
// ── Second-pass refinement (this iteration) ──────────────────────────────
// The first prototype scaled almost every tolerance with the pipe radius R.
// That conflates two physically different quantities:
//
//     R           - GEOMETRIC SCALE of the structure  (step, curvature,
//                    smoothing, dimension-change detection)
//     σ_surface   - OBSERVATION UNCERTAINTY of THIS point cloud (radial
//                    surface thickness / photogrammetric noise) - largely
//                    independent of R
//
// A Ø800 mm pipe does not have 16× the radial scan noise of a Ø50 mm pipe.
// This version:
//   • estimates a robust σ_surface (MAD of radial residuals), global with a
//     slow update, inflated when the visible arc is too narrow to trust it;
//   • drives the radial candidate band, robust-loss scale, inlier threshold
//     and classification band from σ_surface (+ absolute floor, + a loose
//     cap relative to R) instead of a flat 0.45·R;
//   • judges residual as residual/σ_surface, never residual/R, and no longer
//     hard-stops on a large residual alone;
//   • separates OBSERVATION confidence (how well do the local points describe
//     a pipe surface) from CONTINUATION confidence (how plausibly is this the
//     same pipe continuing) - loose on the former, strict on the latter;
//   • measures the two-parameter centre-fit condition number as an
//     identifiability signal;
//   • bounds unsupported extrapolation by an ABSOLUTE distance in metres;
//   • decouples the user's direction-vector *length* from the init ROI;
//   • widens the radius clamp to a real utility range and guards degeneracy
//     by the seed arc span instead of a hidden 20 cm ceiling;
//   • balances dense photogrammetric patches by angular-sector prior weights.
//
// Design commitment (spec §23):  LOOSE ON OBSERVATION NOISE,
//                                STRICT ON STRUCTURAL CONTINUITY.
// Deterministic: no random sampling anywhere in this module.
// ─────────────────────────────────────────────────────────────────────────

const EPS = 1e-9;
const ZUP = new THREE.Vector3(0, 0, 1);

// Tunables, grouped by their real physical meaning (spec §4). Reasoned
// engineering defaults - NOT yet validated against production point clouds
// (spec §34: calibrate the model, never a single example).
export const TRACKING_DEFAULTS = Object.freeze({
	// ═══ GEOMETRIC SCALE — correctly relative to the pipe radius R ═══════
	stepLengthFactor: 2.0,            // advance ≈ 2R per step
	roiRadialFactor: 3.5,             // ROI radius ≈ 3.5R around the predicted axis
	roiAxialFactor: 1.6,             // ROI half-length ≈ 1.6·step
	subBinCount: 3,                  // axial sub-bins for the per-step tangent
	hardBendDegPerStep: 26,          // per-step prediction / tangent-fit turn clamp (limitTurn)
	smoothingPasses: 8,
	smoothingLambda: 0.35,
	smoothingMaxDeviationFactor: 0.30,
	// Line error budget (centre-line AND top-line together, see buildPipeLines): the representation error of the
	// decimated lines vs the smoothed dense lines is at most min(decimateToleranceFactor·R, max(lineTolSigmaFactor·σ_surface,
	// lineTolMinM)), never above lineTolMaxM. I.e. below the surface noise the data can resolve, a fraction of the pipe size,
	// and a few cm in absolute terms.
	decimateToleranceFactor: 0.12,
	lineTolSigmaFactor: 1.5,
	lineTolMinM: 0.005,
	lineTolMaxM: 0.025,
	// The top line is the highest point (world +Z) of the circular cross-section. It is only well defined while the axis is
	// not (near-)vertical: tracking stops before a section steeper than this (stop reason "too_steep"), and a start on a
	// steeper pipe is refused (status "too_steep").
	maxAxisInclinationDeg: 75,
	// Superimposed surfaces (trackDirection): a section that turns more than overlapTurnDeg off the prediction while its
	// surface point count jumps above overlapDensityRatio × the median of ALL accepted sections is two surfaces at
	// once (a crossing / converging pipe of the same size): it may not steer the trajectory (it is coasted through).
	overlapTurnDeg: 2,
	overlapDensityRatio: 1.7,
	// Development diagnostics (per-section data, decision trace, classification, timings). OFF in normal use; never stored.
	debug: false,
	radiusChangeTolerance: 0.20,     // |impliedR − globalR|/R sustained = dimension change
	radiusChangeRunLength: 3,
	endTrimMinAxialSupport: 0.50,
	// Post-refine end-trim: a trailing accepted section whose REVISED
	// sameObjectScore (reviseStationConfidence writes it into contConfidence)
	// is below this is trimmed from the very end. The tracking loop itself now
	// refuses a non-same-object section outright (it never becomes a normal
	// accepted section), so this is a secondary safety net for a marginal tail.
	endTrimMinContinuation: 0.80,
	minAxialSupport: 0.30,
	predictionCurvatureBlend: 0.7,   // weight of the arc-extrapolated prediction vs straight
	predHistoryPoints: 8,            // accepted centres used for the robust line-fit prediction
	predArcMinResidualFactor: 0.2,   // recent centres must bow > this·R off their best-fit line before an ARC prediction is used
	predTangentTurnMinDeg: 1.5,      // recent accepted TANGENTS must rotate ≥ this per step (consistently) to drive a curvature prediction
	predTangentConsistency: 0.85,    // … with the per-step rotation axes this aligned (0-1)

	// ═══ SURFACE-NOISE SCALE — relative to σ_surface, NOT to R ══════════
	bandSigmaFactor: 3.0,            // candidate/inlier band = 3·σ_surface …
	bandMinAbsoluteM: 0.008,        // … but ≥ 8 mm …
	bandMaxRadiusFactor: 0.60,      // … and ≤ 0.60·R (a huge σ can't swallow the pipe)
	robustLossSigmaFactor: 1.6,     // Cauchy loss scale = 1.6·σ_surface
	sigmaSurfaceUpdateAlpha: 0.25,  // EWMA weight of each accepted section's own σ
	sigmaSurfaceFloorM: 0.003,      // 3 mm hard floor
	sigmaSurfaceCeilRadiusFactor: 0.5, // σ can't exceed 0.5·R (that's not a pipe surface)
	lowCoverageSigmaInflateDeg: 130,// below this arc, σ_surface is inflated (arc optimism)

	// (maxUnsupportedDistanceM / maxUnsupportedRadiusFactor REMOVED — the fixed
	// reach-across is now the adaptive reacquireBudget, see SAME-OBJECT group.)

	// ═══ INITIALISATION ════════════════════════════════════════════════
	initMinInliers: 40,
	initMinCoverageDeg: 40,
	initResidualToNoiseMax: 4.0,    // init gate on residual/σ, not residual/R
	initSeedMinArcDeg: 30,          // degeneracy guard: seed points must span ≥ this arc
	initSeedArcMarginDeg: 15,       // start candidates: the clicked arc ± 15° of the circumference (the rest may be soil/clutter)
	initSeedResidualSigmaFactor: 4, // the seed clicks must lie on the fitted surface: median |dist − R| ≤ max(4σ, 0.25R)
	initSeedResidualRadiusFactor: 0.25,
	initRadiusSplitMaxRel: 0.25,    // radius identifiability: two interleaved inlier halves may differ ≤ 25 % of R
	initMinSagittaToNoise: 2.5,     // supported-arc sagitta R·(1−cos(span/2)) must be ≥ 2.5·σ_surface (else the radius is noise-biased)
	initClickedArcRadiusMaxRel: 0.30, // data-fit radius vs the arc fitted to ALL points across the clicks: |Δ| / max ≤ 30 %
	initClickedArcAxialLocalM: 0.10,  // the clicked arc's bend is read from points within ±10 cm (along the axis) of each click
	initRadiusMinM: 0.010,          // Ø20 mm  (was a hidden 2 cm)
	initRadiusMaxM: 1.000,          // Ø2 m    (was a hidden 20 cm)
	initRoiRadialFactor: 3.5,
	initRoiAxialSeedSpreadFactor: 2.5, // init ROI axial = 2.5·(seed axial spread) …
	initRoiAxialRadiusFactor: 6,       // … at least 6·r0 …
	initRoiAxialMinM: 0.30,            // … at least 30 cm …
	initRoiAxialMaxM: 4.0,             // … at most 4 m.  (|d0| is NOT used)
	initAnnulusInner: 0.40,
	initAnnulusOuter: 1.60,

	// ═══ OBSERVED SURFACE SUPPORT — contiguous, radially-consistent arcs ═
	// (spec: root-cause fix for false ~360° coverage - see surfaceSupport.js)
	surfaceSectorCount: 36,              // 10° bins, matches the retired computeAngularCoverage convention
	surfaceMinSectorPointsAbs: 3,        // a sector needs at least this many points …
	surfaceMinSectorDensityFrac: 0.15,   // … or this fraction of the section's own mean points/sector
	surfaceRadialConsistencyFactor: 0.85,// a sector's own median radius must sit within 0.85·band of R
	surfaceGapSectors: 1,                // bridge a single missing 10° bin (sensor shadow), not more
	surfaceMinRunSectors: 2,             // a lone supported sector (10°) is not a "surface", need ≥20°
	surfaceAnchorBlendAlpha: 0.3,        // how fast the running anchor direction may move per step
	classificationInlierThreshold: 0.5,  // surfaceInlierWeight cutoff for the pragmatic binary label

	// ═══ ITERATIVE CONSISTENCY LOOP (spec metode 1-2-4-8-14) ════════════
	// Replaces the one-pass globalConsistencyRefine. Bounded, deterministic,
	// explicit convergence. Per pass: regularise the centre-line → re-fit each
	// section against its REGULARISED-neighbour frame → recompute observed
	// surface support AT the corrected centre (limitation #4) → blend a
	// surface-supported centre in by a continuous trust weight → re-estimate a
	// robust global R (identifiability-weighted) + σ → longitudinal angular-
	// continuity filter on the masks → converged?
	consistencyMaxPasses: 3,
	consistencyLeashFactor: 0.8,        // in-loop leash: a genuinely drifted section MAY move this·R back toward the majority + curvature prior (relaxed vs the 0.30·R output-smoothing leash)
	consistencyMoveCapBands: 1.5,       // max centre move per pass, in σ-bands (anti-oscillation)
	consistencyMoveConvergeRel: 0.02,   // converged when the largest centre move in a pass is < this·R …
	consistencyRConvergeRel: 0.004,     // … and |ΔR|/R is below this
	centerGlobalPriorWeight: 0.15,      // pull of the regularised centre-line prior in the centre combination
	fbDisagreeGoodRel: 0.10,            // fwd/bwd independent-centre disagreement ≤ this·R → section fully trusted
	fbDisagreeBadRel: 0.50,             // … ≥ this·R → strongly down-weighted, and an end-trim candidate

	// ═══ SURFACE-TRUST WEIGHT (spec metode 3 - replaces the surfaceRefineCenter
	// on/off). Continuous weight in [0, surfaceRefineMaxWeight] on how much the
	// surface-only centre may pull the section centre, from LOCAL evidence:
	// supported arc span, min/max supported-sector occupancy (density balance -
	// the guard against the density-skew failure, stress test [4]), effective
	// surface sample size, surface-fit identifiability, and agreement with the
	// angularly-balanced full fit. Set 0 to disable surface influence entirely
	// (reproduces the old surfaceRefineCenter:false default). ═══════════════
	surfaceRefineMaxWeight: 0.6,
	surfaceTrustSpanZeroDeg: 60,
	surfaceTrustSpanFullDeg: 170,
	surfaceTrustBalanceFull: 0.35,     // min/max supported-sector occupancy at/above this → full balance term
	surfaceTrustAgreeGood: 0.35,       // |fullFit − surfaceFit| / band ≤ this → the two fits agree
	surfaceTrustAgreeBad: 1.5,         // … ≥ this → they disagree; don't let the surface move the centre alone

	// ═══ AMBIGUITY / USER ASSISTANCE (spec metode 10, 13) ══════════════
	ambiguitySecondArcRel: 0.5,        // 2nd radially-consistent surface run ≥ this × the chosen run …
	ambiguityMinSeparationDeg: 40,     // … and its centre separated by more than this → competing continuation

	refinementInlierCap: 500,          // deterministic-stride subsample cap for every in-loop refit population (spec §P)

	// ═══ OBSERVATION CONFIDENCE (loose) — "do the points describe a pipe" ═
	obsCoverageZeroDeg: 25,
	obsCoverageFullDeg: 170,
	obsResidualToNoiseGood: 1.3,    // residual/σ ≤ this → term 1
	obsResidualToNoiseBad: 5.5,     // residual/σ ≥ this → term 0  (deliberately loose)
	obsKappaGood: 5,                // centre-fit 2×2 condition number
	obsKappaBad: 60,
	obsMinPointFloor: 12,           // absolute minimum points for any observation at all

	// axial-support anchors for observationConfidence (a half-empty ROI window
	// is a DATA-availability problem, not a "different structure" problem)
	obsAxialSupportGood: 0.55,
	obsAxialSupportBad: 0.12,
	// structural-jump thresholds for the consistency vector's diagnostic count
	structuralBendDeg: 18,
	structuralCenterJumpRel: 0.90,

	// ═══ ACCEPT / COAST / STOP ═════════════════════════════════════════
	// A section is accepted as the SAME pipe only when the local observation is
	// good AND it continues the ESTABLISHED multi-section trajectory
	// (sameObjectScore). A locally-good cylinder that does NOT continue the
	// established pipe is refused - the tracker holds the hypothesis line and
	// searches ahead for the same pipe instead of committing to it.
	acceptObservationConfidence: 0.22,  // loose: a weak-but-real section still counts

	// ═══ TRAJECTORY TRUST (spec: robust reacquisition through a NOISY stretch).
	// A section that IS the pipe (strong obs + same-object) but whose LOCAL FIT
	// is not geometrically trustworthy - ambiguous (several competing arcs),
	// built from very few surface points, or with poor axial support - must NOT
	// become a trajectory ANCHOR. Anchoring on it corrupts the coast prediction
	// so the search for the pipe further on lands in empty space. Instead the
	// tracker HOLDS the last trustworthy hypothesis line, coasts, and reacquires
	// the pipe on the clean far side of the disturbed zone. ═══════════════════
	// a fit is NOT trusted to STEER the trajectory only when it is BOTH strongly
	// ambiguous (several competing arcs of similar strength) AND built from very
	// few classified surface points - i.e. the pipe seen through a genuinely
	// disturbed zone. A merely narrow arc, or a scene with a persistent parallel
	// / crossing pipe (secondArcRel ~0.5-1.5), still anchors normally.
	trajMaxSecondArcRel: 2.5,   // competing-arc strength must EXCEED this …
	trajMinSurfacePoints: 60,   // … AND surface points be BELOW this, to demote a section
	trajMaxBendDeg: 40,         // a single-step bend beyond this also demotes (independent)
	// widening search while coasting (the ROI grows AHEAD, not just wider):
	reacquireRoiAxialGrowth: 0.3, // ROI half-length grows by this × (distCoasted/R) while coasting
	reacquireRoiAxialMax: 2,      // … capped here (bigger straddles observation gaps → stutter)
	reacquireConeStartM: 0.55,    // after this much blind coast, sweep a heading CONE for the pipe
	reacquireConeDeg: 18,         // half-angle of that sweep
	reacquireConeMaxTries: 6,     // bound the extra ROI queries
	reacquireConeMinObsConf: 0.5, // a cone hit must be this solid (not gap-edge scatter) to be taken
	lowTrustBudgetBonusM: 0.5,    // extra coast allowance while a low-trust-real observation keeps confirming the pipe is here

	// ═══ SAME-OBJECT CONTINUITY (spec: follow the SAME physical pipe, not the
	// next locally-plausible cylinder). A DISTINCT axis from observation
	// confidence, measured against the established MULTI-SECTION pipe hypothesis
	// (not the previous step) and the surface anchor carried from the pipe we
	// are already following. During ANY non-accept step the tracker HOLDS the
	// hypothesis trajectory - it never adopts a weak / different-object local
	// fit's centre or tangent (that adoption was the object-switch drift). ════
	sosLatGoodSigma: 0.8,          // |fitted centre − predicted centre|⟂ / predictionσ ≤ this → on-trajectory
	sosLatBadSigma: 3.0,
	sosTangGoodDeg: 4,             // fitted tangent vs the MULTI-SECTION predicted tangent
	sosTangBadDeg: 24,
	sosRadiusGoodRel: 0.08,
	sosRadiusBadRel: 0.30,
	sosSurfContGoodDeg: 20,        // supported arc still overlaps the carried anchor (top of the SAME pipe) …
	sosSurfContBadDeg: 95,         // … drifted this far from it → the surface moved to another object
	sosCurvGoodDeg: 8,            // chord (lastCentre → section) within this of the predicted heading → plausible
	sosCurvBadDeg: 35,           // … this far off → an implausible turn is required to reach the section
	acceptSameObjectScore: 0.45,   // accept a section as the SAME pipe only at/above this
	stopSameObjectScore: 0.18,     // a strong local cylinder scoring below this = object_switch signature
	sosCriticalFloor: 0.20,        // lateral-offset OR surface-continuity term below this is individually disqualifying
	sanityBendDeg: 55,             // absolute garbage stop: a single-step turn sharper than this vs the last strong tangent
	sanityCenterJumpFactor: 3.0,   // absolute garbage stop: fitted centre this many R off the prediction

	// prediction-uncertainty of the HELD trajectory: σ(d) = R·base + growth·d,
	// widening the reacquisition search the longer the pipe is unobserved.
	predSigmaBaseRel: 0.35,
	predSigmaGrowthPerR: 0.18,

	// ADAPTIVE reach-across: a longer / more stable established track earns a
	// longer reach before the search for the SAME pipe is abandoned. Replaces
	// the fixed maxUnsupportedDistanceM / maxUnsupportedRadiusFactor.
	reacquireBaseM: 0.45,
	reacquirePerStableSectionM: 0.12,
	reacquireMaxM: 3.0,
	reacquireMaxRadiusFactor: 24,   // coast (bridged gap) at most 24·R …
	reacquireMaxMinM: 0.6,          // … but at least 0.6 m (small pipes)
	reacquireRoiRadialGrowth: 0.6, // ROI radial extent grows by this × (predσ/R − base) while coasting

	// ═══ RETROSPECTIVE REVISION (spec: future evidence decides the past) ═════
	interiorDropSos: 0.20,         // post-loop: an accepted section whose REVISED sameObjectScore is below this …
	interiorDropCoverageDeg: 90,   // … AND whose supported arc collapsed below this (change-point) → dropped
	objSwitchRunLen: 2,            // … but only when ≥ this many collapsed low-score sections run consecutively (one curvature/step-spike artifact is not a switch)

	// ═══ APPEARANCE (spec: point-cloud RGB as ADDITIONAL, ADAPTIVE evidence -
	// geometry defines the hypothesis, surface supports it, appearance helps
	// only when it is actually discriminative). Auto-disabled when the cloud
	// carries no per-point colour. NEVER a hard filter, NEVER identity. ═══════
	appearanceEnabled: true,
	appInitSupportedSections: 2,    // build the model from the first N supported sections + init
	appMaxSosInfluence: 0.35,       // ceiling on how much appearance can pull a same-object score
	appMaxSurfaceInfluence: 0.5,    // ceiling on how much appearance can down-weight a surface point
	appSepGoodStrength: 0.45,       // separation ≥ this (model vs background/candidate) → colour is informative
	appSampleCap: 160,             // per-section colour samples fed to the appearance model (stride-decimated) - keeps appearance off the hot path
	...APPEARANCE_DEFAULTS,
});

function clamp01(x){ return Math.max(0, Math.min(1, x)); }
function clamp(x, lo, hi){ return Math.max(lo, Math.min(hi, x)); }
function ramp(x, zeroAt, oneAt){
	if(oneAt === zeroAt) return x >= oneAt ? 1 : 0;
	return clamp01((x - zeroAt) / (oneAt - zeroAt));
}
function invRamp(x, oneAt, zeroAt){ return 1 - ramp(x, oneAt, zeroAt); }
function geomMean(terms){
	let logSum = 0;
	for(const t of terms) logSum += Math.log(Math.max(t, 1e-6));
	return clamp01(Math.exp(logSum / terms.length));
}
// ── median / vægtet median ─────────────────────────────────────────────────────────────────────────
// Definitionen er uændret (medianen af de sorterede værdier; for lige antal gennemsnittet af de to midterste; den vægtede median er den
// første sorterede værdi, hvor den kumulerede vægt når halvdelen af totalen). Store arrays sorteres dog ikke længere helt: medianen
// findes med quickselect (O(n)). Den oprindelige sorterings-udgave (…Sorted) bruges til små arrays og som reserve, hvis input ikke er
// helt almindeligt (NaN/Infinity, negative eller ikke-endelige vægte) eller et vægtet resultat ligger så tæt på halvdelen, at
// flydende-komma-summeringsrækkefølgen kunne afgøre valget - så er resultatet altid det samme som før.
const SELECT_MIN_N = 40;

export function medianSorted(values){
	if(values.length === 0) return 0;
	const s = [...values].sort((a, b) => a - b);
	const n = s.length;
	return n % 2 ? s[(n - 1) / 2] : (s[n / 2 - 1] + s[n / 2]) / 2;
}
export function weightedMedianSorted(values, weights){
	if(values.length === 0) return 0;
	const pairs = values.map((v, i) => [v, weights ? weights[i] : 1]).sort((a, b) => a[0] - b[0]);
	const total = pairs.reduce((s, p) => s + p[1], 0);
	let acc = 0;
	for(const [v, w] of pairs){ acc += w; if(acc >= total / 2) return v; }
	return pairs[pairs.length - 1][0];
}

// k'te mindste værdi (0-baseret) i en Float64Array, på stedet (Hoare quickselect, median-af-tre pivot). Efter kaldet er a[i] <= a[k]
// for i < k og a[i] >= a[k] for i > k.
function selectKth(a, k){
	let lo = 0, hi = a.length - 1;
	while(hi > lo){
		const x = a[lo], y = a[(lo + hi) >> 1], z = a[hi];
		const pivot = x < y ? (y < z ? y : (x < z ? z : x)) : (x < z ? x : (y < z ? z : y));
		let i = lo, j = hi;
		while(i <= j){
			while(a[i] < pivot) i++;
			while(a[j] > pivot) j--;
			if(i <= j){ const tmp = a[i]; a[i] = a[j]; a[j] = tmp; i++; j--; }
		}
		if(k <= j) hi = j;
		else if(k >= i) lo = i;
		else return a[k];
	}
	return a[k];
}

export function median(values){
	const n = values.length;
	if(n === 0) return 0;
	if(n < SELECT_MIN_N) return medianSorted(values);
	const a = new Float64Array(n);
	for(let i = 0; i < n; i++){
		const x = values[i];
		if(!Number.isFinite(x)) return medianSorted(values);
		a[i] = x;
	}
	const k = n >> 1;
	const upper = selectKth(a, k);
	if(n % 2) return upper;
	let lower = -Infinity;
	for(let i = 0; i < k; i++) if(a[i] > lower) lower = a[i];
	return (lower + upper) / 2;
}

export function weightedMedian(values, weights){
	const n = values.length;
	if(n === 0) return 0;
	if(n < SELECT_MIN_N) return weightedMedianSorted(values, weights);
	const v = new Float64Array(n), w = new Float64Array(n);
	let total = 0;
	for(let i = 0; i < n; i++){
		const x = values[i], wi = weights ? weights[i] : 1;
		if(!Number.isFinite(x) || !Number.isFinite(wi) || wi < 0) return weightedMedianSorted(values, weights);
		v[i] = x; w[i] = wi; total += wi;
	}
	if(!(total > 0)) return weightedMedianSorted(values, weights);
	const half = total / 2;
	const exact = !weights;                 // enhedsvægte: heltalssummer er eksakte - ingen usikkerhedszone
	const tol = exact ? 0 : total * 1e-9;   // ellers: grænsetilfælde afgøres af den oprindelige (sorterede) udgave
	let lo = 0, hi = n - 1, below = 0;      // below = vægten af alle værdier mindre end [lo..hi]
	while(lo <= hi){
		const x = v[lo], y = v[(lo + hi) >> 1], z = v[hi];
		const pivot = x < y ? (y < z ? y : (x < z ? z : x)) : (x < z ? x : (y < z ? z : y));
		// 3-vejs partition: [lo..lt-1] < pivot, [lt..gt] == pivot, [gt+1..hi] > pivot
		let lt = lo, gt = hi, i = lo;
		while(i <= gt){
			const vi = v[i];
			if(vi < pivot){
				const tv = v[lt]; v[lt] = vi; v[i] = tv;
				const tw = w[lt]; w[lt] = w[i]; w[i] = tw;
				lt++; i++;
			}else if(vi > pivot){
				const tv = v[gt]; v[gt] = vi; v[i] = tv;
				const tw = w[gt]; w[gt] = w[i]; w[i] = tw;
				gt--;
			}else i++;
		}
		let wl = 0, we = 0;
		for(let k = lo; k < lt; k++) wl += w[k];
		for(let k = lt; k <= gt; k++) we += w[k];
		const a = below + wl, b = a + we;
		if(exact){
			if(a >= half){ hi = lt - 1; continue; }
			if(b >= half) return pivot;
			below = b; lo = gt + 1;
		}else{
			if(a >= half + tol){ hi = lt - 1; continue; }
			if(a > half - tol) return weightedMedianSorted(values, weights);
			if(b >= half + tol) return pivot;
			if(b > half - tol) return weightedMedianSorted(values, weights);
			below = b; lo = gt + 1;
		}
	}
	return weightedMedianSorted(values, weights);
}

// Projicerer punkter på planen (u,v) omkring `origin` som parallelle talarrays (+ evt. aksial koordinat langs tAxis) - ingen allokering
// pr. punkt. Samme aritmetik og rækkefølge som p.clone().sub(origin).dot(u) osv., dvs. bit-identiske værdier.
function projectXY(points, origin, u, v, tAxis = null){
	const n = points.length;
	const xs = new Float64Array(n), ys = new Float64Array(n), ts = tAxis ? new Float64Array(n) : null;
	const ox = origin.x, oy = origin.y, oz = origin.z;
	for(let i = 0; i < n; i++){
		const p = points[i];
		const dx = p.x - ox, dy = p.y - oy, dz = p.z - oz;
		xs[i] = dx * u.x + dy * u.y + dz * u.z;
		ys[i] = dx * v.x + dy * v.y + dz * v.z;
		if(ts) ts[i] = dx * tAxis.x + dy * tAxis.y + dz * tAxis.z;
	}
	return {xs, ys, ts};
}
function mean(values){
	return values.length ? values.reduce((a, b) => a + b, 0) / values.length : null;
}
function angleBetweenDeg(a, b){
	const d = Math.max(-1, Math.min(1, a.clone().normalize().dot(b.clone().normalize())));
	return Math.acos(d) * 180 / Math.PI;
}

// Rotate `dir` back towards `ref` so it deviates by at most `maxDeg`
// (hard curvature fail-safe - a soft prior does the gentle shaping).
function limitTurn(dir, ref, maxDeg){
	const r = ref.clone().normalize();
	const c = dir.clone().normalize();
	const ang = angleBetweenDeg(c, r);
	if(ang <= maxDeg || ang < 1e-6) return c;
	const axis = new THREE.Vector3().crossVectors(r, c);
	if(axis.lengthSq() < EPS) return c;
	axis.normalize();
	return r.applyAxisAngle(axis, maxDeg * Math.PI / 180).normalize();
}

// Angular-sector prior weights (spec §10): 1/√(points in that point's own
// 36° … no, 36-bin) sector, around a given centre. Caps how much a dense
// photogrammetric patch on one small arc can dominate the fit / the noise
// estimate versus geometrically better-distributed points.
function angularSectorPriorWeightsXY(xs, ys, sectorCount = 36){
	const n = xs.length;
	const counts = new Array(sectorCount).fill(0);
	const sectors = new Array(n);
	for(let i = 0; i < n; i++){
		let a = Math.atan2(ys[i], xs[i]);
		if(a < 0) a += Math.PI * 2;
		const s = Math.min(sectorCount - 1, Math.floor(a / (Math.PI * 2) * sectorCount));
		counts[s]++;
		sectors[i] = s;
	}
	const out = new Array(n);
	for(let i = 0; i < n; i++) out[i] = 1 / Math.sqrt(Math.max(1, counts[sectors[i]]));
	return out;
}
function angularSectorPriorWeights(points2D, sectorCount = 36){
	const counts = new Array(sectorCount).fill(0);
	const sectors = points2D.map((p) => {
		let a = Math.atan2(p.y, p.x);
		if(a < 0) a += Math.PI * 2;
		const s = Math.min(sectorCount - 1, Math.floor(a / (Math.PI * 2) * sectorCount));
		counts[s]++;
		return s;
	});
	return sectors.map((s) => 1 / Math.sqrt(Math.max(1, counts[s])));
}

// Robust radial surface thickness σ_surface (spec §3). MAD of radial
// residuals e_i = d_i − R, scaled to a Gaussian-equivalent σ, with:
//   • an absolute floor (no scan here is truly noise-free),
//   • an upper ceiling relative to R (beyond that it isn't a pipe surface),
//   • inflation when the visible arc is narrow - a small arc systematically
//     under-samples the true radial spread and makes σ look optimistic.
export function robustSigmaSurface(radialResiduals, priorWeights, coverageDeg, opts){
	if(radialResiduals.length < 4) return opts.sigmaSurfaceFloorM;
	const med = weightedMedian(radialResiduals, priorWeights);
	const absDev = radialResiduals.map((r) => Math.abs(r - med));
	const mad = weightedMedian(absDev, priorWeights);
	let sigma = 1.4826 * mad;
	if(coverageDeg != null && coverageDeg < opts.lowCoverageSigmaInflateDeg){
		sigma *= Math.sqrt(opts.lowCoverageSigmaInflateDeg / Math.max(coverageDeg, 15));
	}
	return Math.max(sigma, opts.sigmaSurfaceFloorM);
}

// Radial candidate / inlier / classification band from σ_surface, not R.
function bandFromSigma(sigma, radius, opts){
	return clamp(opts.bandSigmaFactor * sigma, opts.bandMinAbsoluteM, opts.bandMaxRadiusFactor * radius);
}

// Taubin algebraic circle fit (Chernov's reference form). Far lower bias
// than the Kåsa fit (cylinderFit.js's fitCircle2D) on PARTIAL ARCS and under
// heavy radial noise - which is exactly the regime the init radius estimate
// sits in when only the top of a buried pipe is visible (spec §9). Optional
// per-point weights. Returns {cx, cy, r} in the input frame, or null.
//
// Scale-free: the points are centred on their weighted centroid and divided by
// their RMS distance from it before the moments are formed, so every moment,
// polynomial coefficient and degeneracy test below is dimensionless and O(1).
// A 10 mm half-circle and a 1 m half-circle are then the same numerical problem
// (absolute thresholds on moments of different dimension rejected valid small
// circles), and large world coordinates never enter the polynomial.
const TAUBIN_REL_EPS = 1e-12;
const TAUBIN_MAX_RADIUS_REL = 1e4; // r / RMS spread above this = effectively collinear points (no finite circle)
export function fitCircleTaubin(points2D, weights = null){
	const n = points2D.length;
	if(n < 3) return null;
	let W = 0, mx = 0, my = 0;
	for(let i = 0; i < n; i++){
		const w = weights ? weights[i] : 1;
		mx += w * points2D[i].x; my += w * points2D[i].y; W += w;
	}
	if(!(W > 0) || !isFinite(W)) return null;
	mx /= W; my /= W;

	let spread = 0;
	for(let i = 0; i < n; i++){
		const w = weights ? weights[i] : 1;
		const x = points2D[i].x - mx, y = points2D[i].y - my;
		spread += w * (x * x + y * y);
	}
	const s = Math.sqrt(spread / W);
	if(!(s > 0) || !isFinite(s)) return null; // all points coincide
	const inv = 1 / s;

	let Mxx = 0, Myy = 0, Mxy = 0, Mxz = 0, Myz = 0, Mzz = 0;
	for(let i = 0; i < n; i++){
		const w = weights ? weights[i] : 1;
		const x = (points2D[i].x - mx) * inv, y = (points2D[i].y - my) * inv;
		const z = x * x + y * y;
		Mxx += w * x * x; Myy += w * y * y; Mxy += w * x * y;
		Mxz += w * x * z; Myz += w * y * z; Mzz += w * z * z;
	}
	Mxx /= W; Myy /= W; Mxy /= W; Mxz /= W; Myz /= W; Mzz /= W;

	const Mz = Mxx + Myy;
	const CovXy = Mxx * Myy - Mxy * Mxy;
	const VarZ = Mzz - Mz * Mz;
	const A3 = 4 * Mz;
	const A2 = -3 * Mz * Mz - Mzz;
	const A1 = VarZ * Mz + 4 * CovXy * Mz - Mxz * Mxz - Myz * Myz;
	const A0 = Mxz * (Mxz * Myy - Myz * Mxy) + Myz * (Myz * Mxx - Mxz * Mxy) - VarZ * CovXy;
	const A22 = A2 + A2, A33 = A3 + A3 + A3;

	// Newton on the characteristic polynomial from x = 0 (Chernov). Everything is
	// dimensionless here (Mz = 1 by construction), so the tolerances are relative.
	let x = 0, y0 = A0;
	for(let it = 0; it < 30; it++){
		const yv = A0 + x * (A1 + x * (A2 + x * A3));
		if(Math.abs(yv) > Math.abs(y0)) break;
		const dy = A1 + x * (A22 + x * A33);
		if(Math.abs(dy) < TAUBIN_REL_EPS) break;
		const xn = x - yv / dy;
		if(!isFinite(xn)) break;
		if(Math.abs(xn - x) <= TAUBIN_REL_EPS * Math.max(1, Math.abs(xn))){ x = xn; break; }
		x = xn < 0 ? 0 : xn;
		y0 = yv;
	}
	const det = x * x - x * Mz + CovXy;
	// relative to the scale of its terms: det -> 0 means the points are (nearly) collinear
	const detScale = Math.max(x * x, Math.abs(x * Mz), Math.abs(CovXy), TAUBIN_REL_EPS);
	if(Math.abs(det) <= TAUBIN_REL_EPS * detScale) return null;
	const cx = (Mxz * (Myy - x) - Myz * Mxy) / det / 2;
	const cy = (Myz * (Mxx - x) - Mxz * Mxy) / det / 2;
	const r = Math.sqrt(Math.max(0, cx * cx + cy * cy + Mz));
	if(!(r > 0) || !isFinite(r) || r > TAUBIN_MAX_RADIUS_REL) return null;
	return {cx: cx * s + mx, cy: cy * s + my, r: r * s};
}

// 2-DOF robust fit of a circle CENTRE with KNOWN radius R (Gauss-Newton on
// f_i = ‖p_i − c‖ − R, robust IRLS + optional per-point prior weights).
// Also returns `kappa`, the 2×2 normal-equations condition number: large κ
// ⇒ the centre is well-constrained in one direction and nearly free in the
// other (the identifiability signature of a narrow visible arc - spec §8).
export function fitFixedRadiusCenter(points2D, radius, centerGuess, options = {}){
	const n = points2D.length;
	const xs = new Float64Array(n), ys = new Float64Array(n);
	for(let i = 0; i < n; i++){ xs[i] = points2D[i].x; ys[i] = points2D[i].y; }
	return fitFixedRadiusCenterXY(xs, ys, radius, centerGuess, options);
}

// Samme fit med x/y som parallelle talarrays (varm sti: ingen {x,y}-objekter pr. punkt); fitFixedRadiusCenter er en tynd indpakning.
export function fitFixedRadiusCenterXY(xs, ys, radius, centerGuess, options = {}){
	const n = xs.length;
	const iterations = options.iterations || 8;
	const robustScale = options.robustScale || Math.max(radius * 0.08, 0.01);
	const prior = options.priorWeights || null;
	let cx = centerGuess.x, cy = centerGuess.y;
	let H00 = 1, H01 = 0, H11 = 1, sumW = 0;

	for(let it = 0; it < iterations; it++){
		H00 = 0; H01 = 0; H11 = 0; let g0 = 0, g1 = 0; sumW = 0;
		for(let i = 0; i < n; i++){
			const dx = xs[i] - cx, dy = ys[i] - cy;
			const dist = Math.hypot(dx, dy);
			if(dist < EPS) continue;
			const nx = dx / dist, ny = dy / dist;
			const f = dist - radius;
			const w = robustWeight(Math.abs(f), robustScale) * (prior ? prior[i] : 1);
			H00 += w * nx * nx; H01 += w * nx * ny; H11 += w * ny * ny;
			g0 += w * f * nx; g1 += w * f * ny;
			sumW += w;
		}
		if(sumW < EPS) return null;
		const det = H00 * H11 - H01 * H01;
		if(Math.abs(det) < EPS) return null;
		const dcx = (g0 * H11 - g1 * H01) / det;
		const dcy = (H00 * g1 - H01 * g0) / det;
		cx += dcx; cy += dcy;
		if(Math.hypot(dcx, dcy) < radius * 1e-5) break;
	}

	// eigenvalues of the symmetric 2×2 [[H00,H01],[H01,H11]]
	const tr = H00 + H11, dt = H00 * H11 - H01 * H01;
	const disc = Math.sqrt(Math.max(0, tr * tr / 4 - dt));
	const l1 = tr / 2 + disc, l2 = tr / 2 - disc;
	const kappa = l2 > EPS ? l1 / l2 : Infinity;

	return {cx, cy, kappa, lambdaMin: l2, sumW};
}

// Circumcircle of three 3D points (for curvature-aware prediction, spec §22).
function circleThrough3D(a, b, c){
	const ab = b.clone().sub(a), ac = c.clone().sub(a);
	const abXac = new THREE.Vector3().crossVectors(ab, ac);
	const denom = 2 * abXac.lengthSq();
	if(denom < EPS) return null; // (near-)collinear
	const toCenter = abXac.clone().cross(ab).multiplyScalar(ac.lengthSq())
		.add(ac.clone().cross(abXac).multiplyScalar(ab.lengthSq()))
		.divideScalar(denom);
	return {center: a.clone().add(toCenter), radius: toCenter.length(), normal: abXac.clone().normalize()};
}

// Predict the station `reach` metres ahead along the ESTABLISHED trajectory.
// ROBUST: fits a LINE to the last K accepted centres (principal axis), so a
// straight pipe extrapolates straight and is IMMUNE to the spurious curvature a
// 3-point circle picks up from centre-fit noise - the bug that flung the
// reacquisition search off the pipe over a long coast `reach`. An arc is used
// ONLY when the recent history deviates from that line by more than
// predArcMinResidualFactor·R (a genuine bend), and even then the turn over
// `reach` is clamped. `reach` is one step normally, `step + distCoasted` while
// coasting so the search marches AHEAD along the same pipe.
// `tangents` (optional, parallel to `history`) drives a CURVATURE-FROM-TANGENTS
// prediction: fitted section tangents are far less noisy than centre positions,
// so a consistent per-step tangent rotation follows a gentle curve that the
// centre-line residual test above would miss - the case where a straight coast
// walks off a curving pipe into empty space (real-data hv_vand_2).
function predictAhead(history, tangent, reach, radius, opts, tangents = null){
	const n = history.length;
	const last = history[n - 1];
	if(n < 3) return {center: last.clone().addScaledVector(tangent, reach), tangent: tangent.clone()};

	const K = Math.min(n, opts.predHistoryPoints ?? 8);
	const pts = history.slice(n - K);
	const c = new THREE.Vector3();
	for(const p of pts) c.add(p);
	c.multiplyScalar(1 / K);
	// principal axis by a few power iterations of the 3×3 covariance
	let dir = pts[K - 1].clone().sub(pts[0]);
	if(dir.lengthSq() < EPS) dir = tangent.clone();
	dir.normalize();
	for(let it = 0; it < 4; it++){
		const cov = new THREE.Vector3();
		for(const p of pts){ const d = p.clone().sub(c); cov.addScaledVector(d, d.dot(dir)); }
		if(cov.lengthSq() > EPS) dir.copy(cov.normalize());
	}
	if(dir.dot(tangent) < 0) dir.negate();
	let res2 = 0;
	for(const p of pts){
		const d = p.clone().sub(c);
		res2 += d.clone().addScaledVector(dir, -d.dot(dir)).lengthSq();
	}
	const lineRms = Math.sqrt(res2 / K);

	const straightC = last.clone().addScaledVector(dir, reach);
	const straightT = limitTurn(dir, tangent, opts.hardBendDegPerStep);

	// mean spacing of the recent centres - the "step" the tangent history turned over
	let stepGuess = 0;
	for(let i = 1; i < pts.length; i++) stepGuess += pts[i].distanceTo(pts[i - 1]);
	stepGuess = pts.length > 1 ? stepGuess / (pts.length - 1) : reach;

	// CURVATURE FROM TANGENTS: if the recent accepted tangents rotate by a
	// consistent per-step angle about a consistent axis, integrate that arc over
	// `reach`. Robust where the centre-line residual is too small to trip the
	// bow test below (a long, gentle curve), which is exactly when a straight
	// coast walks off the pipe.
	if(tangents && tangents.length >= 6 && stepGuess > EPS){
		const T = tangents.slice(-Math.min(tangents.length, K));
		const turns = [], axes = [];
		for(let i = 1; i < T.length; i++){
			const dDeg = angleBetweenDeg(T[i - 1], T[i]);
			const ax = new THREE.Vector3().crossVectors(T[i - 1], T[i]);
			if(ax.lengthSq() > EPS){ turns.push(dDeg); axes.push(ax.normalize()); }
		}
		if(turns.length >= 4){
			const md = median(turns);
			const a0 = axes[axes.length - 1];
			const axm = new THREE.Vector3();
			for(const a of axes) axm.addScaledVector(a, a.dot(a0) >= 0 ? 1 : -1);
			const axisConsistency = axm.length() / axes.length;      // ~1 when all aligned
			let clustered = 0;
			for(const d of turns) if(d >= 0.4 * md && d <= 2.5 * md) clustered++;
			clustered /= turns.length;
			if(md >= (opts.predTangentTurnMinDeg ?? 1.5)
				&& axisConsistency >= (opts.predTangentConsistency ?? 0.85)
				&& clustered >= 0.6){
				axm.normalize();
				const steps = reach / stepGuess;
				const turnCapDeg = opts.hardBendDegPerStep * Math.max(1, Math.min(steps, 6));
				let totalDeg = Math.min(md * steps, turnCapDeg);
				const nSub = Math.max(1, Math.min(64, Math.round(Math.max(steps, 1) * 4)));
				const subLen = reach / nSub;
				const qSub = new THREE.Quaternion().setFromAxisAngle(axm, (totalDeg / nSub) * Math.PI / 180);
				let p = last.clone(), t = tangent.clone();
				for(let s = 0; s < nSub; s++){ p.addScaledVector(t, subLen); t.applyQuaternion(qSub); t.normalize(); }
				const w = opts.predictionCurvatureBlend ?? 0.7;
				let pt = tangent.clone().multiplyScalar(1 - w).addScaledVector(t, w);
				pt = pt.lengthSq() < EPS ? dir.clone() : pt.normalize();
				return {
					center: straightC.clone().multiplyScalar(1 - w).addScaledVector(p, w),
					tangent: limitTurn(pt, tangent, turnCapDeg),
				};
			}
		}
	}

	// genuine bend? the recent centres bow away from their own best-fit line
	if(lineRms > (opts.predArcMinResidualFactor ?? 0.25) * radius){
		const arc = circleThrough3D(pts[0], pts[Math.floor(K / 2)], last);
		if(arc && arc.radius > EPS && arc.radius < 5000 * Math.max(reach, radius)){
			const rVec = last.clone().sub(arc.center);
			const rLen = rVec.length();
			if(rLen > EPS){
				let dTheta = reach / rLen;
				const maxTheta = opts.hardBendDegPerStep * Math.PI / 180;
				if(dTheta > maxTheta) dTheta = maxTheta;
				const prevVec = pts[0].clone().sub(arc.center);
				const sgn = Math.sign(new THREE.Vector3().crossVectors(prevVec, rVec).dot(arc.normal)) || 1;
				const qn = new THREE.Quaternion().setFromAxisAngle(arc.normal, sgn * dTheta);
				const arcC = arc.center.clone().add(rVec.clone().applyQuaternion(qn));
				const arcT = rVec.clone().applyQuaternion(qn).cross(arc.normal).multiplyScalar(-sgn).normalize();
				const w = opts.predictionCurvatureBlend;
				let pt = tangent.clone().multiplyScalar(1 - w).addScaledVector(arcT, w);
				pt = pt.lengthSq() < EPS ? dir.clone() : pt.normalize();
				return {
					center: straightC.clone().multiplyScalar(1 - w).addScaledVector(arcC, w),
					tangent: limitTurn(pt, tangent, opts.hardBendDegPerStep),
				};
			}
		}
	}
	return {center: straightC, tangent: straightT};
}

// One local cross-section fit at a predicted station. Radius fixed (R). The
// radial band is driven by `ctx.sigmaSurface` (not R). Returns
// {ok:false, reason} or a full section object carrying ALL sub-diagnostics
// separately (spec §7) so the caller - and later real-data calibration - can
// see WHY a section is weak.
// `queryPoints` may be sync (returns Vector3[]) or async (returns a Promise of
// Vector3[]) - the viewer adapter (pipeRoiAdapter.js) loads octree data on
// demand and is async; the synthetic test slicer is sync. `await` handles both.
export async function fitLocalSection(queryPoints, predCenter, predTangent, radius, opts, ctx = {}){
	const sigma = ctx.sigmaSurface || bandFromSigma(radius * 0.05, radius, opts) / opts.bandSigmaFactor;
	const band = bandFromSigma(sigma, radius, opts);
	const lossScale = Math.max(opts.robustLossSigmaFactor * sigma, opts.bandMinAbsoluteM);

	// while coasting the held trajectory, the search ROI widens with the
	// prediction uncertainty (ctx.roiRadialScale ≥ 1) so a pipe that drifted
	// laterally within the unobserved span can still be reacquired.
	const roiRadial = radius * opts.roiRadialFactor * (ctx.roiRadialScale || 1);
	const step = radius * opts.stepLengthFactor;
	const roiAxial = step * opts.roiAxialFactor;
	// the QUERY slab can reach further ahead while coasting (ctx.roiAxialScale ≥ 1)
	// so a pipe that resumed a bit off the predicted line is still found;
	// axialSupport below is still measured against the BASE half-length.
	const roiAxialQuery = roiAxial * (ctx.roiAxialScale || 1);

	const raw = await queryPoints(predCenter.clone(), predTangent.clone(), roiRadial, roiAxialQuery);
	if(raw.length < opts.obsMinPointFloor) return {ok: false, reason: "insufficient_points", pointCount: raw.length};

	// radial annulus around the PREDICTED axis (which continues the pipe's
	// own history straight/curved) - THIS is what keeps the tracker on the
	// original structure when another pipe crosses or runs alongside.
	const annulus = raw.filter((p) => Math.abs(distanceToAxis(p, predCenter, predTangent) - radius) <= band);
	if(annulus.length < opts.obsMinPointFloor) return {ok: false, reason: "insufficient_annulus_points", pointCount: annulus.length};

	const ts = annulus.map((p) => paramAlongAxis(p, predCenter, predTangent));
	const tSpan = Math.max(...ts) - Math.min(...ts);
	const axialSupport = Math.min(1, tSpan / (2 * roiAxial));

	// ── centre via fixed-R robust fit, plane ⟂ predicted tangent ──
	let {u, v} = orthonormalBasis(predTangent);
	// annulus projiceret på (u,v)-planen + aksial parameter t, uden allokering pr. punkt
	const projRel = (origin, bu, bv) => projectXY(annulus, origin, bu, bv, predTangent);
	let proj = projRel(predCenter, u, v);
	const prior = angularSectorPriorWeightsXY(proj.xs, proj.ys);
	const cFit = fitFixedRadiusCenterXY(proj.xs, proj.ys, radius, {x: 0, y: 0}, {robustScale: lossScale, priorWeights: prior});
	if(!cFit) return {ok: false, reason: "center_fit_failed", pointCount: annulus.length};
	let center = predCenter.clone().addScaledVector(u, cFit.cx).addScaledVector(v, cFit.cy);
	let kappa = cFit.kappa;

	// ── tangent: fixed-R centre per axial sub-bin, line through them ──
	let tangent = predTangent.clone().normalize();
	if(axialSupport >= 0.45 && opts.subBinCount >= 2){
		proj = projRel(center, u, v);
		let tMin = Infinity, tMax = -Infinity;
		for(let i = 0; i < proj.ts.length; i++){ tMin = Math.min(tMin, proj.ts[i]); tMax = Math.max(tMax, proj.ts[i]); }
		const binCentres = [];
		for(let b = 0; b < opts.subBinCount; b++){
			const lo = tMin + (tMax - tMin) * (b / opts.subBinCount);
			const hi = tMin + (tMax - tMin) * ((b + 1) / opts.subBinCount);
			const subIdx = [];
			for(let i = 0; i < proj.ts.length; i++) if(proj.ts[i] >= lo && proj.ts[i] <= hi) subIdx.push(i);
			if(subIdx.length < 8) continue;
			const subX = new Float64Array(subIdx.length), subY = new Float64Array(subIdx.length);
			for(let k = 0; k < subIdx.length; k++){ subX[k] = proj.xs[subIdx[k]]; subY[k] = proj.ys[subIdx[k]]; }
			const subPrior = subIdx.map((i) => prior[i]);
			const sc = fitFixedRadiusCenterXY(subX, subY, radius, {x: 0, y: 0}, {robustScale: lossScale, priorWeights: subPrior});
			if(!sc) continue;
			let tMean = 0; for(let k = 0; k < subIdx.length; k++) tMean += proj.ts[subIdx[k]]; tMean /= subIdx.length;
			binCentres.push(center.clone().addScaledVector(u, sc.cx).addScaledVector(v, sc.cy).addScaledVector(tangent, tMean));
		}
		if(binCentres.length >= 2){
			const dir = binCentres[binCentres.length - 1].clone().sub(binCentres[0]);
			if(dir.lengthSq() > EPS){
				let cand = dir.normalize();
				if(cand.dot(predTangent) < 0) cand.negate();
				tangent = limitTurn(cand, predTangent, opts.hardBendDegPerStep);
				({u, v} = orthonormalBasis(tangent));
				const reproj = projectXY(annulus, predCenter, u, v);
				const c2 = fitFixedRadiusCenterXY(reproj.xs, reproj.ys, radius, {x: 0, y: 0}, {robustScale: lossScale, priorWeights: prior});
				if(c2){ center = predCenter.clone().addScaledVector(u, c2.cx).addScaledVector(v, c2.cy); kappa = c2.kappa; }
			}
		}
	}

	// ── metrics (each kept separate) ──
	let inMask = annulus.filter((p) => Math.abs(distanceToAxis(p, center, tangent) - radius) <= band);
	if(inMask.length < opts.obsMinPointFloor) return {ok: false, reason: "insufficient_inliers", pointCount: inMask.length};

	// ── OBSERVED SURFACE SUPPORT (root-cause fix for false ~360° "coverage"
	// on a real, partially-buried pipe - see surfaceSupport.js's own header).
	// `coverageDeg` from here on MEANS supported coverage: a contiguous,
	// sufficiently dense, radially-consistent arc, preferring continuity with
	// the running anchor direction (previous section's own arc, or the
	// user's seed points at init - carried in via ctx.anchorDirWorld).
	// `rawCoverageDeg` (old bin-occupancy measure) is kept as a diagnostic.
	//
	// The centre here stays the angularly-balanced full-`inMask` fit above.
	// SURFACE-supported centre correction is NOT done during tracking - it is
	// done later in iterativeConsistencyRefine, by a CONTINUOUS trust weight
	// with the regularised global centre-line as a counterweight (spec metode
	// 3). Doing it here, greedily, with no global prior, is what over-trusted a
	// single densely-reconstructed patch on an evenly-shaped pipe (stress test
	// [4]); the loop has the counterweight this step lacks. ──
	const computeSupport = (c) => {
		const proj = projectXY(inMask, c, u, v);
		let anchorAngleRad = null;
		if(ctx.anchorDirWorld){
			const refDir = ctx.anchorDirWorld.clone().projectOnPlane(tangent);
			if(refDir.lengthSq() > EPS) anchorAngleRad = Math.atan2(refDir.dot(v), refDir.dot(u));
		}
		return {support: computeSurfaceSupportXY(proj.xs, proj.ys, radius, band, opts, anchorAngleRad), proj};
	};

	const {support, proj: proj2D} = computeSupport(center);
	const sectorOf = (x, y, sectorCount) => {
		let a = Math.atan2(y, x);
		if(a < 0) a += Math.PI * 2;
		return Math.min(sectorCount - 1, Math.floor(a / ((Math.PI * 2) / sectorCount)));
	};
	const surfacePoints = inMask.filter((_, i) => support.supportedSectorMask[sectorOf(proj2D.xs[i], proj2D.ys[i], support.sectorCount)]);
	if(inMask.length < opts.obsMinPointFloor) return {ok: false, reason: "insufficient_inliers", pointCount: inMask.length};

	const inlierPrior = angularSectorPriorWeightsXY(proj2D.xs, proj2D.ys);
	const dists = inMask.map((p) => distanceToAxis(p, center, tangent));
	const residuals = dists.map((d) => d - radius);
	// sector-balanced RMS so a dense patch doesn't dominate the residual
	let wRms = 0, wSum = 0;
	for(let i = 0; i < residuals.length; i++){ wRms += inlierPrior[i] * residuals[i] * residuals[i]; wSum += inlierPrior[i]; }
	const radialRMS = Math.sqrt(wRms / Math.max(wSum, EPS));
	const impliedRadius = weightedMedian(dists, inlierPrior);

	let anchorDirWorld = ctx.anchorDirWorld || null;
	if(support.arcCenterAngle != null){
		const newDir = u.clone().multiplyScalar(Math.cos(support.arcCenterAngle)).addScaledVector(v, Math.sin(support.arcCenterAngle));
		anchorDirWorld = anchorDirWorld
			? anchorDirWorld.clone().multiplyScalar(1 - opts.surfaceAnchorBlendAlpha).addScaledVector(newDir, opts.surfaceAnchorBlendAlpha).normalize()
			: newDir;
	}

	// VISIBLE ARC for radius-identifiability (spec §8 §11). Once the FIXED
	// radius is biased (a partial arc under heavy noise), coverage around the
	// offset fitted centre can look near-complete and hide that only a
	// fraction of the circumference was ever scanned. The honest visible arc
	// is a property of the SCAN, ~constant along a buried pipe, so the
	// smaller of "this section's own supported coverage" and "what init
	// already established" is used.
	const visibleArcDeg = ctx.initCoverageDeg != null
		? Math.min(ctx.initCoverageDeg, support.supportedCoverageDeg)
		: support.supportedCoverageDeg;
	const sectionSigma = robustSigmaSurface(residuals, inlierPrior, support.supportedCoverageDeg, opts);
	const bendDeg = angleBetweenDeg(tangent, predTangent);
	const centerJump = center.clone().sub(predCenter).projectOnPlane(predTangent).length();
	const globalR = ctx.globalRadius || radius;
	const radiusDeviationRel = Math.abs(impliedRadius - globalR) / globalR;
	// Radius identifiability / uncertainty (spec §8). Two independent parts,
	// take the larger:
	//   1. STATISTICAL - sagitta-error propagation. For an arc of half-angle
	//      φ, a sagitta error δh ≈ σ_surface/√N_eff maps to a radius error
	//      δR ≈ (4/φ²)·δh (from R = (c²/4 + h²)/(2h), dR/dh ≈ −4/φ² for
	//      small φ). This is the CRLB-shaped term - right magnitude AND right
	//      monotonic growth as the arc narrows.
	//   2. SYSTEMATIC - a narrow noisy arc also carries a large FRACTIONAL
	//      bias that no local statistic can see (both halves of the arc are
	//      biased the same way, so a bootstrap agrees on the wrong answer).
	//      Modelled as a distrust prior on the radius that grows as coverage
	//      falls below a full view - calibrated to the observed algebraic
	//      circle-fit bias, kept here in one place (spec §8 §34).
	const effN = Math.max(1, wSum);
	const phi = Math.max(visibleArcDeg, 8) * Math.PI / 360; // half the visible arc, rad
	const statSigmaR = (4 / (phi * phi)) * (sectionSigma / Math.sqrt(effN));
	// Fractional distrust of the radius from a partial arc, shaped to the
	// algebraic circle fit's measured partial-arc bias (≈ −6% at 180°, −23%
	// at 135°, −45% at 90° for a 20%-noise surface - see pipeTracking.stress
	// test [1]). Below a near-complete view the radius simply cannot be
	// trusted, and the tracker must report that.
	const arcBiasBase = Math.max(0, 1 - visibleArcDeg / 185);
	const arcBiasFraction = clamp(1.4 * arcBiasBase ** 1.3, 0, 0.6);
	const radiusUncertainty = Math.max(statSigmaR, radius * arcBiasFraction);

	// APPEARANCE samples (spec): only if the point cloud carries per-point RGB.
	// surfaceColors = colours of the SPECIFIC surface population; fitColors
	// = colours of ROI points that are NOT pipe surface (soil / clutter / other
	// pipes) - the reference for "is colour informative here" (sep_bg).
	const hasAppearance = raw.length > 0 && raw[0] && raw[0].rgb != null;
	let surfaceColors = null, fitColors = null;
	if(hasAppearance){
		// deterministic-stride subsamples, capped small - a colour distribution
		// is well estimated from ~150 samples and this keeps the per-step
		// appearance cost negligible (spec: no new performance cost).
		const APP_CAP = opts.appSampleCap ?? 160;
		surfaceColors = [];
		const sStride = Math.max(1, Math.floor(surfacePoints.length / APP_CAP));
		for(let i = 0; i < surfacePoints.length; i += sStride) if(surfacePoints[i].rgb) surfaceColors.push(surfacePoints[i].rgb);
		// colours of the FULL fit population (radial-band inliers = observed
		// surface + whatever soil / adjacent structure sits at the same radius),
		// used for sep_bg (fraction the model rejects) - works even when soil
		// hugs the pipe at its exact radius and there is no "empty" background.
		fitColors = [];
		const fStride = Math.max(1, Math.floor(inMask.length / APP_CAP));
		for(let i = 0; i < inMask.length; i += fStride) if(inMask[i].rgb) fitColors.push(inMask[i].rgb);
	}

	return {
		ok: true,
		center, tangent, radius,
		hasAppearance,
		surfaceColors,
		fitColors,
		// THREE point populations (spec: LOOSE SEARCH / ROBUST FIT / SPECIFIC
		// SURFACE CLASSIFICATION): `raw` (search, already discarded) was loose;
		// `inliers` (= inMask, the angular-sector-balanced, robust-fit
		// population) drives the centre/tangent fit AND global refinement;
		// `surfacePoints` is the NEW, stricter population - only the points
		// whose angular sector is part of the contiguous OBSERVED surface arc
		// - used for classification / preview highlight, never for fitting.
		inliers: inMask,
		surfacePoints,
		anchorDirWorld,
		supportedSectorMask: support.supportedSectorMask,
		pointCount: inMask.length,
		surfacePointCount: surfacePoints.length,
		roiPointCount: raw.length,
		annulusPointCount: annulus.length,
		// separate observation-quality diagnostics
		radialRMS,
		residualToNoise: radialRMS / Math.max(sectionSigma, opts.sigmaSurfaceFloorM),
		relResidual: radialRMS / radius,     // kept for back-compat / logging only
		sectionSigma,
		coverageDeg: support.supportedCoverageDeg,     // supported (see header comment)
		rawCoverageDeg: support.rawCoverageDeg,          // diagnostic only
		surfaceArcSpanDeg: support.arcSpanDeg,
		// competing-continuation detection (spec metode 10) - the largest OTHER
		// radially-consistent surface run in this cross-section, if any
		secondArcRel: support.secondArcRel,
		secondArcSeparationDeg: support.secondArcSeparationDeg,
		kappa,
		radiusUncertainty,
		// separate continuation diagnostics
		impliedRadius,
		radiusDeviationRel,
		bendDeg,
		centerJump,
		centerJumpRel: centerJump / radius,
		axialSupport,
	};
}

// OBSERVATION confidence (spec §6): how well do the local points describe a
// pipe surface HERE? Deliberately LOOSE - photogrammetric surfaces are thick
// and patchy. Large radial spread is OK if the fit is still identifiable.
// Axial support lives here (not in continuation): a half-empty ROI axial
// window means we simply don't observe much of a cross-section at this
// station - a DATA availability problem that should be coasted across, not a
// "this is a different structure" problem (spec §24).
export function observationConfidence(s, opts){
	return geomMean([
		ramp(s.coverageDeg, opts.obsCoverageZeroDeg, opts.obsCoverageFullDeg),
		invRamp(s.residualToNoise, opts.obsResidualToNoiseGood, opts.obsResidualToNoiseBad),
		invRamp(s.kappa, opts.obsKappaGood, opts.obsKappaBad),
		ramp(s.pointCount, opts.obsMinPointFloor, opts.obsMinPointFloor * 4),
		ramp(s.axialSupport, opts.obsAxialSupportBad, opts.obsAxialSupportGood),
	]);
}

// ── SAME-OBJECT CONTINUITY ──────────────────────────────────────────────
// Lateral/tangential uncertainty of the HELD hypothesis trajectory at a
// coasted distance `d` (metres) from the last strong observation. Grows with
// d so the reacquisition search widens the longer the pipe is unobserved.
function predictionSigma(d, R, opts){
	return R * opts.predSigmaBaseRel + opts.predSigmaGrowthPerR * d;
}

// "Is this section a plausible continuation of the SAME physical pipe?" -
// DISTINCT from observationConfidence. Returns {score in [0,1], + every
// component term and its raw value} so the decision is fully transparent
// (spec: "gør den ikke til en ny uigennemsigtig erstatning for
// continuationConfidence" - GOOD OBSERVATION + BAD SAME-OBJECT AGREEMENT must
// be explicitly readable). Measured against the established multi-section
// prediction `hyp = {center, tangent, sigma}`, the carried surface
// `anchorDirWorld`, and (for the required-curvature term) `prevCenter` = the
// last strongly-observed centre - NEVER against the previous step, which coast
// is allowed to leave stale.
//
//   latTerm  - fitted centre vs predicted trajectory, in prediction-σ units
//   tangTerm - fitted tangent vs the MULTI-SECTION predicted tangent
//   radTerm  - implied radius vs the global radius of the pipe being followed
//   surfTerm - does the observed supported arc still sit on the carried anchor
//   curvTerm - REQUIRED CURVATURE: turn from the established heading needed to
//              REACH this centre (chord vs predicted tangent). A real bend with
//              continuous surface keeps this modest; a jump onto another object
//              needs a big, unsupported turn.
//   appTerm  - APPEARANCE (spec): supplied by the caller, [0,1], NEUTRAL (1)
//              unless the appearance model is confident AND colour is actually
//              discriminative here. NON-critical - appearance alone can neither
//              pass nor fail same-object. Defaults to 1 (no colour / disabled).
// latTerm and surfTerm are each individually DISQUALIFYING (sosCriticalFloor);
// appTerm is NOT.
export function sameObjectScore(section, hyp, globalRadius, anchorDirWorld, opts, prevCenter = null, appTerm = 1){
	const base = {
		score: 0, lat: null, latTerm: 0, tangDeg: null, tangTerm: 0, radRel: null, radTerm: 0,
		curvDeg: null, curvTerm: 1, surfGapDeg: null, surfTerm: 1, appTerm: +appTerm.toFixed(3),
		predSigma: hyp && hyp.sigma != null ? hyp.sigma : null,
	};
	if(!section || !section.ok) return base;
	const tHat = hyp.tangent.clone().normalize();

	const lat = section.center.clone().sub(hyp.center).projectOnPlane(tHat).length();
	const latTerm = invRamp(lat / Math.max(hyp.sigma, EPS), opts.sosLatGoodSigma, opts.sosLatBadSigma);
	const tangDeg = angleBetweenDeg(section.tangent, hyp.tangent);
	const tangTerm = invRamp(tangDeg, opts.sosTangGoodDeg, opts.sosTangBadDeg);
	const radRel = Math.abs(section.impliedRadius - globalRadius) / Math.max(globalRadius, EPS);
	const radTerm = invRamp(radRel, opts.sosRadiusGoodRel, opts.sosRadiusBadRel);

	let curvDeg = 0, curvTerm = 1;
	if(prevCenter){
		const chord = section.center.clone().sub(prevCenter);
		if(chord.lengthSq() > EPS){
			curvDeg = angleBetweenDeg(chord, tHat);
			curvTerm = invRamp(curvDeg, opts.sosCurvGoodDeg, opts.sosCurvBadDeg);
		}
	}

	let surfTerm = 1, surfGapDeg = null;
	const mask = section.supportedSectorMask;
	if(anchorDirWorld && mask && mask.some(Boolean)){
		const {u, v} = orthonormalBasis(section.tangent);
		const a = anchorDirWorld.clone().projectOnPlane(tHat);
		if(a.lengthSq() > EPS){
			let ang = Math.atan2(a.dot(v), a.dot(u)); if(ang < 0) ang += Math.PI * 2;
			const sc = mask.length, sr = (Math.PI * 2) / sc;
			const anchorSector = Math.min(sc - 1, Math.floor(ang / sr));
			let best = sc;
			for(let i = 0; i < sc; i++){
				if(!mask[i]) continue;
				let dd = Math.abs(i - anchorSector); dd = Math.min(dd, sc - dd);
				best = Math.min(best, dd);
			}
			surfGapDeg = best * (360 / sc);
			surfTerm = invRamp(surfGapDeg, opts.sosSurfContGoodDeg, opts.sosSurfContBadDeg);
		}
	}

	const at = Math.max(0, Math.min(1, appTerm));
	const g = geomMean([latTerm, tangTerm, radTerm, surfTerm, curvTerm, at]);
	const critical = Math.min(latTerm, surfTerm); // appTerm is NOT critical
	const score = critical < (opts.sosCriticalFloor ?? 0.2) ? Math.min(g, critical) : g;
	return {
		score: +score.toFixed(4),
		lat, latTerm: +latTerm.toFixed(3),
		tangDeg: +tangDeg.toFixed(1), tangTerm: +tangTerm.toFixed(3),
		radRel: +radRel.toFixed(4), radTerm: +radTerm.toFixed(3),
		curvDeg: +curvDeg.toFixed(1), curvTerm: +curvTerm.toFixed(3),
		surfGapDeg: surfGapDeg == null ? null : +surfGapDeg.toFixed(1), surfTerm: +surfTerm.toFixed(3),
		appTerm: +at.toFixed(3),
		predSigma: +hyp.sigma.toFixed(4),
	};
}

// Adaptive reach-across budget (metres): a longer / more stable established
// track earns more reach before the search for the SAME pipe is abandoned.
// How far the track may coast (hold the predicted line) without observation before it must reacquire or stop. A bridged
// gap is a geometric assumption, so the cap is relative to the pipe size (a stiff large pipe stays straight over a longer
// unseen span than a small one): at most reacquireMaxRadiusFactor·R (not below reacquireMaxMinM), never above reacquireMaxM.
function reacquireBudget(stableSectionCount, R, opts){
	const cap = Math.min(opts.reacquireMaxM, Math.max(opts.reacquireMaxRadiusFactor * R, opts.reacquireMaxMinM));
	return clamp(
		opts.reacquireBaseM + opts.reacquirePerStableSectionM * stableSectionCount,
		Math.min(cap, Math.max(opts.reacquireBaseM, 2 * R)),
		cap);
}

// Follow ONE physical pipe one way from `model`. Core principle (spec): find
// the most plausible continuation of the SAME pipe, not the next locally-
// plausible cylinder. While the pipe is observed and continues the established
// trajectory it is accepted. When observation weakens OR a section does not
// continue the established pipe, the tracker HOLDS the hypothesis trajectory
// (it never adopts the weak / other-object local fit) and searches ahead for
// the same pipe (reacquisition), bounded by an ADAPTIVE reach that scales with
// how long/stable the established track is.
async function trackDirection(queryPoints, model, sign, opts){
	const stations = [];
	let center = model.center.clone();
	let tangent = model.tangent.clone().multiplyScalar(sign).normalize();
	const R = model.radius;
	const step = R * opts.stepLengthFactor;

	let distCoasted = 0;           // metres since the last accepted (strong + same-object) section
	let stableSectionCount = 0;    // accepted sections in a row → earns reach-across
	let radiusOutOfTolRun = 0;
	let switchStationM = null;     // followed distance where an object_switch was first seen
	let switchRunActive = false;   // a switch-like coast is in progress and not yet resolved by a reacquire
	let coneTries = 0;             // heading-cone reacquisition attempts in the current coast run
	let lowTrustBonusUntilM = 0;  // coast distance up to which a low-trust-real observation extends the reacquire budget
	let lastStrongTangent = tangent.clone();
	// decision trace + reacquisition attempts: development diagnostics only (opts.debug); normal runs keep counters
	const debug = !!opts.debug;
	const decisionLog = [];
	const reacquireAttempts = [];
	let reacquiredCount = 0;
	let coastedCount = 0;
	let followedLen = 0;           // length of the accepted centres polyline, kept incrementally
	const acceptedSurfaceCounts = []; // surface point count per accepted section (superimposed-surface check)
	let coastRunStart = null;      // stations[] index where the current coast run began

	// APPEARANCE model, learned from STRONG-supported observations only (spec).
	// Seeded from init inlier colours + the first appInitSupportedSections
	// supported sections; updated conservatively thereafter; NEVER from
	// uncertain / coasted / modelled / wrong-object.
	const appOn = opts.appearanceEnabled;
	const appInitSamples = appOn && model.initColors ? model.initColors.slice() : [];
	// if the user's seed picks carried colour, that is ground truth - build the
	// model NOW from them, before any section can dilute it. Otherwise wait for
	// the first appInitSupportedSections supported sections.
	let appModel = (appOn && model.initColorsFromSeeds && appInitSamples.length >= 10)
		? buildAppearanceModel(appInitSamples, opts) : null;
	if(appModel && !appModel.ok) appModel = null;
	let appInitSectionsSeen = appModel ? opts.appInitSupportedSections : 0;
	const appSepBgHist = [];
	let sigmaSurface = model.sigmaSurface || bandFromSigma(R * 0.05, R, opts) / opts.bandSigmaFactor;
	// running OBSERVED SURFACE ANCHOR (spec: seed anchoring + continuity with
	// the previous section's own arc) - a world-space direction pointing at
	// the middle of the last known-good observed surface arc. Seeded from the
	// user's own surface clicks at init; updated by a slow blend each step so
	// it tracks a genuinely growing/shrinking/shifting arc without being able
	// to snap straight to "the other side" just because clutter is there too.
	let anchorDirWorld = model.anchorDirWorld || null;
	const runningRadii = [R];
	const acceptedCentres = [center.clone()];
	const acceptedTangents = [tangent.clone()]; // parallel to acceptedCentres - drives the curvature-from-tangents prediction
	let stopReason = "completed";

	// live progress for a UI (progress text + live preview line); O(1) per call. After it, `onYield` (if given) lets the host
	// hand the main thread back to the browser - otherwise a run on already-loaded points never yields a frame, so the
	// growing line is never painted and ESC is only seen when the run is over.
	const report = async () => {
		if(opts.onProgress){
			opts.onProgress({
				direction: sign > 0 ? "forward" : "backward",
				acceptedCount: acceptedCentres.length - 1,
				coastedCount,
				followedLengthM: followedLen,
				centers: acceptedCentres, // by reference - read-only, for a live preview polyline
				tip: center, // where the search stands now (the last accepted centre, or ahead of it while bridging a gap)
			});
		}
		if(opts.onYield) await opts.onYield();
	};
	const followedM = () => followedLen;

	const MAX_STEPS = 4000;
	let prefetchedAt = null;   // hvor sidste prefetch blev udsendt (se PREFETCH nedenfor)
	for(let k = 0; k < MAX_STEPS; k++){
		if(opts.shouldCancel && opts.shouldCancel()){ stopReason = "cancelled"; break; }

		// prediction is anchored to the last ACCEPTED centre and marches
		// `step + distCoasted` ahead - so while coasting the search advances
		// along the established trajectory looking for the SAME pipe further on.
		const reach = step + distCoasted;
		const pred = predictAhead(acceptedCentres, tangent, reach, R, opts, acceptedTangents);

		// widen the ROI as the held-trajectory uncertainty grows (spec:
		// "søgeområdet må derfor kunne blive bredere længere fremme") - BOTH
		// radially and AHEAD (axially), so a pipe that resumes a little off the
		// predicted line is still inside the search slab.
		const predSigma = predictionSigma(distCoasted, R, opts);
		const roiRadialScale = Math.max(1, 1 + opts.reacquireRoiRadialGrowth * (predSigma / R - opts.predSigmaBaseRel));
		const roiAxialScale = clamp(1 + opts.reacquireRoiAxialGrowth * (distCoasted / R), 1, opts.reacquireRoiAxialMax);
		const ctx = {
			sigmaSurface, globalRadius: median(runningRadii),
			initCoverageDeg: model.visibleArcDeg != null ? model.visibleArcDeg : model.coverageDeg,
			anchorDirWorld, roiRadialScale, roiAxialScale,
		};
		// PREFETCH (performance fase 2): varm adapterens node-cache for strækningen FREM langs den forudsagte retning (afstand, ikke
		// antal trin - et trin er kun få cm, mens noder er meter store), mens dette trin regnes. Ren indlæsnings-optimering: vælger ingen
		// punkter, ændrer hverken ROI, fit, skridt eller stopkriterier (selve forespørgslen nedenfor er stadig autoritativ), og blokerer
		// aldrig (fire-and-forget). Samme ROI-dimensioner som fitLocalSection. Udsendes højst hver prefetchEveryM meters fremdrift.
		const prefetchAheadM = opts.prefetchAheadM ?? 0;
		if(prefetchAheadM > 0 && typeof queryPoints.prefetch === "function"
			&& (prefetchedAt === null || pred.center.distanceTo(prefetchedAt) >= (opts.prefetchEveryM ?? 0.25))){
			prefetchedAt = pred.center.clone();
			const pfRadial = R * opts.roiRadialFactor * roiRadialScale;
			const pfAxial = step * opts.roiAxialFactor * roiAxialScale;
			const pfSpacing = opts.prefetchSpacingM ?? 0.25;
			for(let d = pfSpacing; d <= prefetchAheadM + 1e-9; d += pfSpacing){
				queryPoints.prefetch(pred.center.clone().addScaledVector(pred.tangent, d), pred.tangent.clone(), pfRadial, pfAxial);
			}
		}
		let section = await fitLocalSection(queryPoints, pred.center, pred.tangent, R, opts, ctx);
		let coneReacquired = false;

		// HEADING-CONE reacquisition: after a stretch of blind coast where the
		// held line found NO data at all (not merely a weak fit), it has probably
		// walked off a curving / diving pipe. Sweep a cone of headings and take
		// the SMALLEST-deviation hit that is both a real observation AND still the
		// SAME pipe (so it cannot chase a branch). Bounded by
		// reacquireConeMaxTries so a genuinely finished pipe still stops. A weak
		// fit on real data (narrow arc, sparse) is NOT a cone trigger - the coast
		// budget handles that and stops early instead of drifting.
		const noDataAtHeld = (!section.ok && section.reason === "insufficient_points")
			|| (section.ok && (section.roiPointCount ?? 0) === 0);
		if(noDataAtHeld
			&& distCoasted > opts.reacquireConeStartM && coneTries < opts.reacquireConeMaxTries){
			coneTries++;
			const anchor = acceptedCentres[acceptedCentres.length - 1];
			const up = new THREE.Vector3(0, 0, 1);
			const axH = new THREE.Vector3().crossVectors(up, pred.tangent);         // yaw (left/right)
			const axV = new THREE.Vector3().crossVectors(pred.tangent, axH);        // pitch (up/down)
			const cone = opts.reacquireConeDeg * Math.PI / 180;
			const trials = [];
			for(const f of [0.5, 1.0]){        // smaller deviations first
				for(const ax of [axH, axV]){
					if(ax.lengthSq() < EPS) continue;
					const axn = ax.clone().normalize();
					for(const s of [f, -f]){
						const q = new THREE.Quaternion().setFromAxisAngle(axn, cone * s);
						const tt = pred.tangent.clone().applyQuaternion(q).normalize();
						trials.push({tt, cc: anchor.clone().addScaledVector(tt, reach), dev: f});
					}
				}
			}
			for(const tr of trials){
				const s = await fitLocalSection(queryPoints, tr.cc, tr.tt, R, opts, ctx);
				if(!s.ok || (s.roiPointCount ?? 0) === 0) continue;
				// a cone hit must be a SOLID re-find of the pipe, not gap-edge
				// scatter - otherwise a real observation gap stutters into hops
				if(observationConfidence(s, opts) < opts.reacquireConeMinObsConf) continue;
				if((s.surfacePointCount ?? s.pointCount ?? 0) < opts.trajMinSurfacePoints) continue;
				const sc = sameObjectScore(s, {center: tr.cc, tangent: tr.tt, sigma: predSigma},
					ctx.globalRadius, anchorDirWorld, opts, anchor, 1).score;
				if(sc < opts.acceptSameObjectScore) continue;
				section = s; pred.center = tr.cc; pred.tangent = tr.tt; coneReacquired = true;
				break;                          // first (smallest-deviation) same-pipe hit wins
			}
		}

		const obsConf = section.ok ? observationConfidence(section, opts) : 0;
		const hyp = {center: pred.center, tangent: pred.tangent, sigma: predSigma};
		const prevAcceptedCentre = acceptedCentres[acceptedCentres.length - 1];

		// APPEARANCE contribution to the same-object decision (spec: adaptive,
		// gated, non-critical). Neutral (appTerm 1) until the model is confident
		// AND colour is discriminative vs THIS candidate's colours - AND ONLY
		// when the GEOMETRY is ambiguous. A section that is geometrically dead-on
		// keeps appTerm ≈ 1 even if its colour differs (a marking / joint must
		// not create a false object-switch).
		const sosGeom = sameObjectScore(section, hyp, ctx.globalRadius, anchorDirWorld, opts, prevAcceptedCentre, 1);
		let appContrib = null, appTerm = 1;
		if(appOn && appModel && section.ok && section.hasAppearance && section.surfaceColors && section.surfaceColors.length >= 8){
			// appearance only bites when the geometry is ambiguous - scaled by the
			// weakest geometric floor so a marking / joint on an otherwise perfect
			// section cannot create a false object-switch
			const geomFloor = Math.min(sosGeom.latTerm, sosGeom.surfTerm, sosGeom.curvTerm);
			const effInfluence = opts.appMaxSosInfluence * (1 - geomFloor);
			const simAlt = appearanceSetSimilarity(appModel, section.surfaceColors, opts);
			appContrib = appearanceContribution({
				model: appModel, similarity: simAlt, separation: simAlt == null ? 0 : 1 - simAlt,
				maxInfluence: effInfluence, opts,
			});
			appTerm = appContrib.appTerm;
		}
		const sos = appTerm >= 0.999
			? sosGeom
			: sameObjectScore(section, hyp, ctx.globalRadius, anchorDirWorld, opts, prevAcceptedCentre, appTerm);
		const atM = followedM() + distCoasted;

		const strongObs = section.ok
			&& obsConf >= opts.acceptObservationConfidence
			&& section.pointCount >= opts.obsMinPointFloor
			&& section.axialSupport >= opts.minAxialSupport;
		const sameObject = section.ok && sos.score >= opts.acceptSameObjectScore;
		// is this local fit trustworthy enough to STEER the trajectory? (spec:
		// robust reacquisition through noise - a real-but-untrustworthy section
		// is observed, but the prediction keeps running on the last good line).
		// Only a section that is BOTH strongly ambiguous AND very thinly
		// supported, OR bent implausibly hard in one step, is demoted.
		const disturbed = section.ok
			&& (section.secondArcRel ?? 0) > opts.trajMaxSecondArcRel
			&& (section.surfacePointCount ?? section.pointCount ?? Infinity) < opts.trajMinSurfacePoints;
		const wildBend = section.ok && (section.bendDeg ?? 0) > opts.trajMaxBendDeg;
		// a deliberate cone reacquisition is already same-object + obs gated - let
		// it anchor even if the fit is marginal, so the crossing does not
		// immediately re-fragment into another coast.
		// two surfaces at once: turning off the prediction while the surface point count jumps far above the median of the
		// accepted sections = a crossing / converging pipe of the same size superimposed on this one (a real bend does
		// not double the surface density). Observed, but not allowed to steer - coasted through on the established line.
		let superimposed = false;
		if(section.ok && acceptedSurfaceCounts.length >= 4 && angleBetweenDeg(section.tangent, pred.tangent) > opts.overlapTurnDeg){
			const med = median(acceptedSurfaceCounts); // whole accepted track: a return from a thinned stretch is not a jump
			superimposed = med > 0 && (section.surfacePointCount ?? 0) > opts.overlapDensityRatio * med;
		}
		const trajTrustOk = section.ok && (coneReacquired || !(disturbed || wildBend || superimposed));
		// a STRONG local cylinder that does NOT continue the established pipe -
		// the object-switch signature (GOOD OBSERVATION + BAD SAME-OBJECT).
		const switchLike = strongObs && sos.score < opts.stopSameObjectScore;

		const logEntry = {
			k, stationM: +atM.toFixed(3), distCoastedM: +distCoasted.toFixed(3),
			predSigmaRel: +(predSigma / R).toFixed(3), roiPoints: section.roiPointCount ?? 0,
			obsConf: +obsConf.toFixed(3), sameObjectScore: sos.score,
			components: {latTerm: sos.latTerm, tangTerm: sos.tangTerm, radTerm: sos.radTerm, surfTerm: sos.surfTerm, curvTerm: sos.curvTerm, appTerm: sos.appTerm,
				lat: sos.lat == null ? null : +sos.lat.toFixed(4), tangDeg: sos.tangDeg, radRel: sos.radRel, curvDeg: sos.curvDeg, surfGapDeg: sos.surfGapDeg},
			appearance: appContrib ? {similarity: appContrib.similarity, strength: appContrib.strength, appTerm: appContrib.appTerm, confidence: appContrib.confidence} : (section.ok && section.hasAppearance ? {available: true, modelReady: false} : {available: false}),
			competingSecondArcRel: section.ok ? +(section.secondArcRel ?? 0).toFixed(2) : null,
		};

		// absolute garbage stops only (true teleport / reversal); the nuanced
		// work is done by sameObjectScore + the adaptive reach.
		if(section.ok && angleBetweenDeg(section.tangent, lastStrongTangent) > opts.sanityBendDeg){
			logEntry.decision = "stop"; logEntry.reason = "bend_too_sharp"; if(debug) decisionLog.push(logEntry);
			stopReason = "bend_too_sharp"; break;
		}
		if(section.ok && section.centerJumpRel > opts.sanityCenterJumpFactor){
			logEntry.decision = "stop"; logEntry.reason = "center_unstable"; if(debug) decisionLog.push(logEntry);
			stopReason = "center_unstable"; break;
		}

		if(strongObs && sameObject && trajTrustOk){
			// the top line is only defined while the axis is not (near-)vertical: stop BEFORE a steeper section instead of
			// producing a centre-line the top line cannot follow (see computeTopLine)
			if(axisInclinationDeg(section.tangent) > opts.maxAxisInclinationDeg){
				logEntry.decision = "stop"; logEntry.reason = "too_steep"; if(debug) decisionLog.push(logEntry);
				stopReason = "too_steep"; break;
			}
			// ── ACCEPT: an observed continuation of the SAME pipe. If we had
			//    been coasting, this also CLOSES the gap = reacquisition. ──
			const reacquired = distCoasted > 0;
			if(reacquired){
				// retro-mark the held-hypothesis coast run as MODELLED geometry:
				// keep it in the centre-line (the pipe passes through here) but it
				// is not directly-observed evidence and never feeds the fit.
				const gapFromM = coastRunStart != null && stations[coastRunStart]
					? (stations[coastRunStart].stationM ?? atM - distCoasted) : atM - distCoasted;
				for(let j = (coastRunStart ?? stations.length); j < stations.length; j++){
					if(stations[j] && stations[j].coasted){
						stations[j].state = "modelled";
						stations[j].modelled = true;
						stations[j].keepInCenterline = true;
					}
				}
				reacquiredCount++;
				if(debug) reacquireAttempts.push({
					atStationM: +atM.toFixed(2), gapLengthM: +distCoasted.toFixed(2), gapFromM: +Number(gapFromM).toFixed(2),
					decision: "reacquired", switchRetracted: switchRunActive || undefined,
					candidate: {obsConf: +obsConf.toFixed(3), sameObjectScore: sos.score, components: logEntry.components},
				});
				// a switch-like coast that ENDS in a strong same-object reacquisition
				// was the tracker coasting past a fitting / joint, NOT an object
				// switch - retract the signal so it is not reported as one.
				if(switchRunActive){ switchStationM = null; switchRunActive = false; }
			}
			distCoasted = 0;
			coastRunStart = null;
			switchRunActive = false;
			coneTries = 0;
			lowTrustBonusUntilM = 0;
			if(section.radiusDeviationRel > opts.radiusChangeTolerance){
				if(++radiusOutOfTolRun >= opts.radiusChangeRunLength){
					logEntry.decision = "stop"; logEntry.reason = "radius_changed"; if(debug) decisionLog.push(logEntry);
					stopReason = "radius_changed"; break;
				}
			}else{
				radiusOutOfTolRun = 0;
				runningRadii.push(section.impliedRadius);
			}
			sigmaSurface = clamp(
				(1 - opts.sigmaSurfaceUpdateAlpha) * sigmaSurface + opts.sigmaSurfaceUpdateAlpha * section.sectionSigma,
				opts.sigmaSurfaceFloorM, opts.sigmaSurfaceCeilRadiusFactor * R);
			center = section.center;
			tangent = section.tangent;
			lastStrongTangent = tangent.clone();
			anchorDirWorld = section.anchorDirWorld;
			followedLen += center.distanceTo(acceptedCentres[acceptedCentres.length - 1]);
			acceptedCentres.push(center.clone());
			if(section.surfacePointCount != null) acceptedSurfaceCounts.push(section.surfacePointCount);
			acceptedTangents.push(tangent.clone());
			stableSectionCount++;

			// APPEARANCE model: learn ONLY from a SUPPORTED accept, and NEVER
			// from one reached across a gap on its first evidence (that could be
			// the wrong object) - spec's anti-feedback-loop rule.
			let appModelUpdated = false;
			const scols = section.hasAppearance && section.surfaceColors && section.surfaceColors.length >= 8 ? section.surfaceColors : null;
			if(appOn && scols && !reacquired){
				if(appModel){
					// only LEARN from a section whose own surface colours are
					// mostly consistent with what the model already knows - a
					// section that is heavily off-colour is contaminated (soil at
					// the pipe radius, a wrong-object arc) and must not move the
					// authoritative model (spec's anti-feedback / anti-contamination).
					const badFrac = appearanceMismatchFraction(appModel, scols, opts);
					if(badFrac <= 0.35){
						appModel = updateAppearanceModel(appModel, scols, opts);
						appModelUpdated = true;
					}
				}else{
					for(const c of scols) appInitSamples.push(c);
					appInitSectionsSeen++;
					if(appInitSectionsSeen >= opts.appInitSupportedSections && appInitSamples.length >= 24){
						appModel = buildAppearanceModel(appInitSamples, opts);
						appModelUpdated = appModel && appModel.ok;
					}
				}
			}
			// how INFORMATIVE colour is for classification here = the FRACTION of
			// the pipe-radius fit population whose colour the model rejects. High
			// ⇒ colour is identifying a large non-pipe fraction inside the band
			// (soil at the pipe radius, an adjacent structure); ~0 ⇒ the band is
			// already all pipe-colour (or pipe and background share a colour) and
			// appearance adds nothing.
			let appSepBg = null;
			if(appOn && appModel && appModel.ok && section.fitColors && section.fitColors.length >= 20){
				appSepBg = appearanceMismatchFraction(appModel, section.fitColors, opts);
				appSepBgHist.push(appSepBg);
			}

			stations.push({
				...sectionSummary(section), coasted: false,
				confidence: geomMean([obsConf, sos.score]), obsConfidence: obsConf, contConfidence: sos.score,
				sameObjectScore: sos.score, sameObjectComponents: logEntry.components,
				appearanceSimilarity: appContrib ? appContrib.similarity : null,
				appearanceStrength: appContrib ? appContrib.strength : null,
				appearanceTerm: sos.appTerm,
				appearanceSepBg: appSepBg == null ? null : +appSepBg.toFixed(3),
				appearanceModelUpdated: appModelUpdated || undefined,
				state: "supported", reacquired: reacquired || undefined,
			});
			logEntry.decision = reacquired ? "reacquired" : "accept";
			if(debug) decisionLog.push(logEntry);
			await report();
			continue;
		}

		// ── NOT a clean same-pipe accept → UNCERTAIN. Hold the hypothesis
		//    trajectory; do NOT adopt this fit's centre/tangent. Search ahead. ──
		if(debug && section.ok){
			// a fitted candidate that was NOT taken - record WHY (incl. whether
			// appearance actually helped reject it, spec §diagnostics)
			reacquireAttempts.push({
				atStationM: +atM.toFixed(2), distSinceStrongM: +distCoasted.toFixed(2),
				candidate: {
					obsConf: +obsConf.toFixed(3), sameObjectScore: sos.score, components: logEntry.components,
					appearance: appContrib ? {similarity: appContrib.similarity, strength: appContrib.strength, appTerm: appContrib.appTerm} : null,
				},
				decision: "rejected",
				reason: !strongObs ? "weak_observation"
					: switchLike ? "wrong_object"
					: (sameObject && !trajTrustOk) ? "low_trust_observation"
					: "insufficient_same_object",
			});
		}
		// a real-but-untrustworthy pipe section: the pipe IS here, just too noisy
		// to steer by. It buys a BOUNDED extra coast allowance (below) so the
		// tracker rides through a localised disturbed zone - but not an unbounded
		// one, or a uniformly-poorly-observed pipe would drift forever.
		const lowTrustReal = strongObs && sameObject && !trajTrustOk;
		if(coastRunStart == null) coastRunStart = stations.length;
		distCoasted += step;
		if(lowTrustReal) lowTrustBonusUntilM = Math.max(lowTrustBonusUntilM, distCoasted + opts.lowTrustBudgetBonusM);
		const budget = reacquireBudget(stableSectionCount, R, opts)
			+ (distCoasted <= lowTrustBonusUntilM ? opts.lowTrustBudgetBonusM : 0);
		if(distCoasted > budget){
			stopReason = switchLike ? "object_switch"
				: section.ok ? "reacquire_failed"
				: (section.axialSupport != null && section.axialSupport < opts.minAxialSupport ? "axial_support_lost" : "no_support");
			if(switchLike && switchStationM == null) switchStationM = followedM();
			logEntry.decision = "stop"; logEntry.reason = stopReason; if(debug) decisionLog.push(logEntry);
			break;
		}
		if(switchLike){ switchRunActive = true; if(switchStationM == null) switchStationM = followedM(); }
		center = pred.center;
		tangent = pred.tangent;
		coastedCount++;
		stations.push({
			center: center.clone(), tangent: tangent.clone(), coasted: true,
			stationM: +atM.toFixed(3), distSinceStrongM: +distCoasted.toFixed(3),
			confidence: geomMean([obsConf, sos.score]), obsConfidence: obsConf, contConfidence: sos.score,
			sameObjectScore: sos.score, sameObjectComponents: logEntry.components,
			appearanceSimilarity: appContrib ? appContrib.similarity : null,
			appearanceStrength: appContrib ? appContrib.strength : null,
			appearanceTerm: sos.appTerm,
			switchLike: switchLike || undefined, lowTrustObs: lowTrustReal || undefined, state: "uncertain",
			reason: section.ok ? undefined : section.reason,
			// the fitted-but-rejected local observation, for diagnostics only -
			// it does NOT feed the model
			rejectedCenter: section.ok ? section.center.clone() : undefined,
			rejectedImpliedRadius: section.ok ? section.impliedRadius : undefined,
			rejectedCoverageDeg: section.ok ? section.coverageDeg : undefined,
		});
		logEntry.decision = "coast"; logEntry.reason = switchLike ? "wrong_object_present"
			: lowTrustReal ? "low_trust_observation"
			: (section.ok ? "insufficient_same_object" : "no_observation");
		if(debug) decisionLog.push(logEntry);
		await report();
	}

	const appModelConfidence = appOn && appModel ? appearanceModelConfidence(appModel, opts) : 0;
	return {
		stations, stopReason, sigmaSurface, switchStationM, decisionLog, reacquireAttempts, reacquiredCount,
		appearance: appOn ? {
			available: appInitSamples.length > 0 || !!appModel,
			model: appModel && appModel.ok ? appModel : null,
			confidence: +appModelConfidence.toFixed(3),
			sepBgMedian: appSepBgHist.length ? +median(appSepBgHist).toFixed(3) : null,
			initSectionsSeen: appInitSectionsSeen,
		} : {available: false},
	};
}

function sectionSummary(s){
	return {
		center: s.center.clone(),
		tangent: s.tangent.clone(),
		radius: s.radius,
		impliedRadius: s.impliedRadius,
		radiusDeviationRel: s.radiusDeviationRel,
		radiusUncertainty: s.radiusUncertainty,
		pointCount: s.pointCount,
		surfacePointCount: s.surfacePointCount,
		roiPointCount: s.roiPointCount,
		annulusPointCount: s.annulusPointCount,
		radialRMS: s.radialRMS,
		relResidual: s.relResidual,
		residualToNoise: s.residualToNoise,
		sectionSigma: s.sectionSigma,
		coverageDeg: s.coverageDeg,
		rawCoverageDeg: s.rawCoverageDeg,
		surfaceArcSpanDeg: s.surfaceArcSpanDeg,
		secondArcRel: s.secondArcRel,
		secondArcSeparationDeg: s.secondArcSeparationDeg,
		kappa: s.kappa,
		bendDeg: s.bendDeg,
		centerJump: s.centerJump,
		centerJumpRel: s.centerJumpRel,
		axialSupport: s.axialSupport,
		inliers: s.inliers,
		surfacePoints: s.surfacePoints,
		supportedSectorMask: s.supportedSectorMask,
		anchorDirWorld: s.anchorDirWorld,
	};
}

// Laplacian smoothing with a hard per-vertex leash to the raw section centres
// (spec §10). Endpoints fixed.
export function smoothCenterline(rawPoints, maxDeviation, opts){
	const n = rawPoints.length;
	if(n <= 2) return rawPoints.map((p) => p.clone());
	let pts = rawPoints.map((p) => p.clone());
	for(let pass = 0; pass < opts.smoothingPasses; pass++){
		const next = pts.map((p) => p.clone());
		for(let i = 1; i < n - 1; i++){
			const lap = pts[i - 1].clone().add(pts[i + 1]).multiplyScalar(0.5).sub(pts[i]);
			next[i].addScaledVector(lap, opts.smoothingLambda);
			const off = next[i].clone().sub(rawPoints[i]);
			if(off.length() > maxDeviation) next[i].copy(rawPoints[i].clone().addScaledVector(off.normalize(), maxDeviation));
		}
		pts = next;
	}
	return pts;
}

// Distance from p to the closed SEGMENT a-b (not the infinite line: a point beyond an end, e.g. on a section that doubles
// back, must count with its real distance).
export function distanceToSegment3D(p, a, b){
	const abx = b.x - a.x, aby = b.y - a.y, abz = b.z - a.z;
	const apx = p.x - a.x, apy = p.y - a.y, apz = p.z - a.z;
	const len2 = abx * abx + aby * aby + abz * abz;
	let t = len2 > 0 ? (apx * abx + apy * aby + apz * abz) / len2 : 0;
	t = t < 0 ? 0 : (t > 1 ? 1 : t);
	const dx = apx - t * abx, dy = apy - t * aby, dz = apz - t * abz;
	return Math.sqrt(dx * dx + dy * dy + dz * dz);
}

// Douglas-Peucker on 3D points with the segment distance above. Returns the KEPT INDICES (ascending, first and last
// always kept); decimatePolyline3D returns the points.
export function decimateIndices3D(points, tol){
	const n = points.length;
	if(n <= 2) return points.map((_, i) => i);
	const keep = new Uint8Array(n);
	keep[0] = keep[n - 1] = 1;
	const stack = [[0, n - 1]];
	while(stack.length){
		const [a, b] = stack.pop();
		if(b <= a + 1) continue;
		let maxD = -1, maxI = -1;
		for(let i = a + 1; i < b; i++){
			const d = distanceToSegment3D(points[i], points[a], points[b]);
			if(d > maxD){ maxD = d; maxI = i; }
		}
		if(maxD > tol){ keep[maxI] = 1; stack.push([a, maxI], [maxI, b]); }
	}
	const out = [];
	for(let i = 0; i < n; i++) if(keep[i]) out.push(i);
	return out;
}
export function decimatePolyline3D(points, tol){
	return decimateIndices3D(points, tol).map((i) => points[i].clone());
}

// Axis inclination above the horizontal plane, in degrees (0 = horizontal, 90 = vertical).
export function axisInclinationDeg(tangent){
	const l = tangent.length();
	if(!(l > 0)) return 0;
	return Math.asin(Math.min(1, Math.abs(tangent.z) / l)) * 180 / Math.PI;
}

// Canonical vertex tangents of a polyline (central differences, one-sided at the ends). The top line is always derived
// from THESE (also when the user edits the centre-line or a stored line is restored), so commit, edit and reload agree.
export function polylineVertexTangents(points){
	return points.map((p, i) => {
		if(points.length === 1) return new THREE.Vector3(1, 0, 0);
		const a = points[Math.max(0, i - 1)], b = points[Math.min(points.length - 1, i + 1)];
		const t = b.clone().sub(a);
		return t.lengthSq() > EPS ? t.normalize() : new THREE.Vector3(1, 0, 0);
	});
}

// Top line: at each centre-line vertex c with tangent t the highest point (world +Z) of the circular cross-section ⟂ t,
//   top = c + R·û,  û = normalize(Z − (Z·t)·t).
// |Z − (Z·t)·t| = cos(inclination), so the highest point is unique while the axis is not vertical and becomes ill-conditioned
// as it approaches vertical. Tracking never produces a section steeper than maxAxisInclinationDeg; for a vertex steeper than
// that (only possible after a manual edit of the centre-line) the up-direction is CARRIED from the nearest well-defined vertex
// and re-projected into the local cross-section, so the point still lies on the pipe surface (never displaced along the
// axis). If no vertex is well defined, a fixed horizontal perpendicular of the first tangent is carried.
export function computeTopLine(centerline, tangents, radius, maxInclinationDeg = TRACKING_DEFAULTS.maxAxisInclinationDeg){
	const n = centerline.length;
	if(n === 0) return [];
	const minHoriz = Math.cos(maxInclinationDeg * Math.PI / 180);
	const ts = centerline.map((_, i) => (tangents[i] || tangents[tangents.length - 1] || ZUP).clone().normalize());
	const ups = ts.map((t) => {
		const up = ZUP.clone().addScaledVector(t, -ZUP.dot(t));
		const l = up.length();
		return l >= minHoriz && l > 1e-9 ? up.multiplyScalar(1 / l) : null;
	});
	const reproject = (dir, t) => {
		const up = dir.clone().addScaledVector(t, -dir.dot(t));
		if(up.lengthSq() > 1e-12) return up.normalize();
		return orthonormalBasis(t).u.clone();
	};
	if(ups.some((u) => u)){
		// carry forward, then backward, from the nearest defined vertex
		for(let i = 1; i < n; i++) if(!ups[i] && ups[i - 1]) ups[i] = reproject(ups[i - 1], ts[i]);
		for(let i = n - 2; i >= 0; i--) if(!ups[i] && ups[i + 1]) ups[i] = reproject(ups[i + 1], ts[i]);
	}else{
		let dir = orthonormalBasis(ts[0]).u.clone();
		for(let i = 0; i < n; i++){ dir = reproject(dir, ts[i]); ups[i] = dir; }
	}
	return centerline.map((c, i) => c.clone().addScaledVector(ups[i], radius));
}

// The two product lines from the refined section centres (in along-pipe order):
//   1. centre-line = Laplacian-smoothed centres (leash ≤ smoothingMaxDeviationFactor·R to the observed centres),
//   2. decimated with the segment Douglas-Peucker,
//   3. top-line = computeTopLine(decimated centre-line, its canonical vertex tangents, R)  - the same function the editor
//      and a restored registration use, so the pair is always consistent.
// Error budget (both lines together): every dense smoothed centre AND its dense top point (from the dense tangents) lies
// within `tol` of the decimated centre-/top-line. Decimating the centre-line moves the vertex tangents and therefore the top
// points, so the tolerance is tightened until the TOP line also meets it (falls back to all dense vertices).
export function buildPipeLines(centres, radius, sigmaSurface, opts){
	if(!centres || centres.length < 2) return null;
	const smoothed = smoothCenterline(centres, radius * opts.smoothingMaxDeviationFactor, opts);
	const tol = clamp(
		Math.min(radius * opts.decimateToleranceFactor, Math.max((sigmaSurface || 0) * opts.lineTolSigmaFactor, opts.lineTolMinM)),
		Math.min(opts.lineTolMinM, radius * opts.decimateToleranceFactor), opts.lineTolMaxM);
	const denseTop = computeTopLine(smoothed, polylineVertexTangents(smoothed), radius, opts.maxAxisInclinationDeg);
	const maxDevTo = (dense, line) => {
		let worst = 0;
		for(const p of dense){
			let best = Infinity;
			for(let j = 0; j < line.length - 1; j++){ const d = distanceToSegment3D(p, line[j], line[j + 1]); if(d < best) best = d; }
			if(best > worst) worst = best;
		}
		return worst;
	};
	let dpTol = tol;
	for(let attempt = 0; attempt < 5; attempt++){
		const idx = attempt < 4 ? decimateIndices3D(smoothed, dpTol) : smoothed.map((_, i) => i);
		const centerline = idx.map((i) => smoothed[i].clone());
		const topLine = computeTopLine(centerline, polylineVertexTangents(centerline), radius, opts.maxAxisInclinationDeg);
		if(attempt === 4 || (maxDevTo(smoothed, centerline) <= tol && maxDevTo(denseTop, topLine) <= tol)){
			return {centerline, topLine, tolerance: tol};
		}
		dpTol *= 0.5;
	}
	return null;
}

// Classification (spec §14, §25): stored as centre-line + radius + band
// (+ σ_surface). `isPipeInlier` is the pragmatic binary; `pipeInlierWeight`
// is the continuous evidence (robust weight of the σ-normalised residual)
// used by global refinement and available to callers that want a soft label.
export function buildClassification(centerline, radius, band, sigmaSurface = null){
	return {center: centerline.map((p) => p.clone()), radius, band, sigmaSurface: sigmaSurface || band / 3};
}
function radialDistanceToCenterline(point, cls){
	const p = point.clone ? point.clone() : new THREE.Vector3(point.x, point.y, point.z);
	let best = Infinity;
	for(let i = 0; i < cls.center.length - 1; i++){
		const a = cls.center[i], b = cls.center[i + 1];
		const ab = b.clone().sub(a);
		const len2 = ab.lengthSq();
		if(len2 < EPS) continue;
		const s = p.clone().sub(a).dot(ab) / len2;
		if(s < 0 || s > 1) continue;
		best = Math.min(best, Math.abs(p.distanceTo(a.clone().addScaledVector(ab, s)) - cls.radius));
	}
	return best;
}
export function isPipeInlier(point, classification){
	return radialDistanceToCenterline(point, classification) <= classification.band;
}
export function pipeInlierWeight(point, classification){
	const d = radialDistanceToCenterline(point, classification);
	if(!isFinite(d)) return 0;
	return robustWeight(d, Math.max(classification.sigmaSurface, 1e-4));
}

// ── initialisation ──────────────────────────────────────────────────────
// Taubin circle, reweighted a few times with Tukey's biweight (scale = robust spread of the residuals, ≥ sigmaFloor), so
// clutter and a second surface inside the window stop pulling the circle. baseWeights (e.g. angular-sector priors) multiply in.
function robustTaubin(points, baseWeights, sigmaFloor){
	let circle = fitCircleTaubin(points, baseWeights), sigma = null;
	for(let it = 0; it < 6 && circle; it++){
		const res = points.map((p) => Math.hypot(p.x - circle.cx, p.y - circle.cy) - circle.r);
		sigma = Math.max(1.4826 * median(res.map(Math.abs)), sigmaFloor);
		const k = 4.685 * sigma;
		const w = res.map((r, i) => (Math.abs(r) < k ? (1 - (r / k) * (r / k)) ** 2 : 0) * (baseWeights ? baseWeights[i] : 1));
		circle = fitCircleTaubin(points, w) || circle;
	}
	return circle && circle.r > 0 ? {circle, sigma} : null;
}

// The arc the user clicked, measured from every point across it: in the cross-section plane (u, v around the clicks' centroid),
// a window spanning the clicks' chord (+25 %) and as deep as half the chord, pooled over ±axialHalf along the axis; a robust
// (Tukey-reweighted) Taubin circle through those points, and the arc's measured sagitta relative to the surface noise (below).
// Uses no radius guess, so a wrong seed circle cannot confirm itself here.
async function estimateClickedArc(queryPoints, axis, u, v, origin, surfacePoints, surf2D, axialHalf, opts){
	let sxx = 0, sxy = 0, syy = 0, mx = 0, my = 0;
	for(const p of surf2D){ mx += p.x; my += p.y; }
	mx /= surf2D.length; my /= surf2D.length;
	for(const p of surf2D){ const dx = p.x - mx, dy = p.y - my; sxx += dx * dx; sxy += dx * dy; syy += dy * dy; }
	const th = 0.5 * Math.atan2(2 * sxy, sxx - syy);
	const ex = Math.cos(th), ey = Math.sin(th);
	const xs = surf2D.map((p) => (p.x - mx) * ex + (p.y - my) * ey);
	const half = (Math.max(...xs) - Math.min(...xs)) / 2, xMid = (Math.max(...xs) + Math.min(...xs)) / 2;
	const W = Math.max(1.25 * half, opts.sigmaSurfaceFloorM), H = Math.max(half, 2 * opts.sigmaSurfaceFloorM);
	const near = await queryPoints(origin.clone().addScaledVector(u, mx).addScaledVector(v, my), axis, Math.hypot(W + Math.abs(xMid), H), axialHalf);
	const pts = [], ts = [];
	for(const p of near){
		const d = p.clone().sub(origin);
		const x = d.dot(u), y = d.dot(v);
		const xe = (x - mx) * ex + (y - my) * ey - xMid, yn = -(x - mx) * ey + (y - my) * ex;
		if(Math.abs(xe) > W || Math.abs(yn) > H) continue;
		pts.push({x, y}); ts.push(d.dot(axis));
	}
	if(pts.length < opts.initMinInliers) return null;
	const fit = robustTaubin(pts, null, opts.sigmaSurfaceFloorM);
	if(!fit) return null;
	const {circle, sigma} = fit;
	// Does the clicked arc bend measurably? Read off the points themselves, not off the fitted circle (a too-small circle
	// makes any strip look curved): the surface position at the two outermost clicks and at the middle click, each the
	// median of the points around that click - close to it in the cross-section AND along the axis (a click is one noisy
	// point; points further along a curving pipe shift sideways in this projection and would fake a bend). The sagitta is
	// the middle's distance from the chord between the two ends, against the robust spread of the points around the middle
	// click (measured in a disk widened until it no longer truncates that spread, at most half the chord).
	const iLo = xs.indexOf(Math.min(...xs)), iHi = xs.indexOf(Math.max(...xs));
	const iMid = xs.reduce((b, x, i) => (Math.abs(x - xMid) < Math.abs(xs[b] - xMid) ? i : b), 0);
	const rho = Math.max(0.25 * half, 2 * opts.sigmaSurfaceFloorM);
	// (on sparse data the axial reach widens step by step until the click has enough neighbours)
	const around = (i, radius = rho) => {
		const t = surfacePoints[i].clone().sub(origin).dot(axis);
		for(const reach of [1, 2, 4].map((f) => f * opts.initClickedArcAxialLocalM).concat(Infinity)){
			const qx = [], qy = [];
			for(let k = 0; k < pts.length; k++){
				const p = pts[k];
				if(Math.abs(ts[k] - t) <= reach && Math.hypot(p.x - surf2D[i].x, p.y - surf2D[i].y) <= radius){ qx.push(p.x); qy.push(p.y); }
			}
			if(qx.length >= 8) return {x: median(qx), y: median(qy), qx, qy};
		}
		return null;
	};
	const A = around(iLo), B = around(iHi), M = around(iMid);
	const flat = {cx: circle.cx, cy: circle.cy, r: circle.r, sigma, pointCount: pts.length, sagittaToNoise: 0};
	if(!A || !B || !M || iMid === iLo || iMid === iHi) return flat;
	const chord = Math.hypot(B.x - A.x, B.y - A.y);
	if(!(chord > 0)) return flat;
	const nx = -(B.y - A.y) / chord, ny = (B.x - A.x) / chord;
	const sagitta = Math.abs((M.x - A.x) * nx + (M.y - A.y) * ny);
	const spreadIn = (nb) => 1.4826 * median(nb.qx.map((x, k) => Math.abs((x - nb.x) * nx + (nb.qy[k] - nb.y) * ny)));
	let midSigma = spreadIn(M);
	for(let r = rho, it = 0; it < 4 && midSigma > r / 3 && r < 0.5 * half; it++){
		r = Math.min(3 * midSigma, 0.5 * half);
		const wide = around(iMid, r);
		if(wide) midSigma = Math.max(midSigma, spreadIn(wide));
	}
	midSigma = Math.max(midSigma, opts.sigmaSurfaceFloorM);
	return {cx: circle.cx, cy: circle.cy, r: circle.r, sigma, pointCount: pts.length, sagitta, sagittaToNoise: sagitta / midSigma};
}

export async function estimateInitialModel(queryPoints, directionPoints, surfacePoints, opts){
	if(!directionPoints || directionPoints.length < 2) return {ok: false, reason: "need_two_direction_points"};
	if(!surfacePoints || surfacePoints.length < 3) return {ok: false, reason: "need_surface_points"};

	const dA = directionPoints[0].clone();
	const dB = directionPoints[directionPoints.length - 1].clone();
	const axis0 = dB.clone().sub(dA);
	if(axis0.length() < 1e-6) return {ok: false, reason: "direction_points_too_close"};
	axis0.normalize();

	const sCentroid = new THREE.Vector3();
	for(const s of surfacePoints) sCentroid.add(s);
	sCentroid.divideScalar(surfacePoints.length);
	const {u, v} = orthonormalBasis(axis0);
	const surf2D = surfacePoints.map((s) => {
		const d = s.clone().sub(sCentroid);
		return {x: d.dot(u), y: d.dot(v)};
	});

	// init ROI axial length from the SEED axial spread + radius + absolute
	// bounds - NOT from |d0| (spec §13): a 0.5 m or a 3 m direction vector
	// pointing the same way must give the same result.
	const seedTs = surfacePoints.map((s) => s.clone().sub(sCentroid).dot(axis0));
	const seedAxialSpread = Math.max(...seedTs) - Math.min(...seedTs);

	// The circle through the 3-5 clicks alone is a poor radius estimate on a partly visible pipe (each click carries the full
	// surface noise, and on a top-only arc a few mm decide between Ø40 and Ø170 - real data, HV Vand 1). The clicked arc is
	// therefore measured from ALL points across the clicks' own extent (independent of any radius guess) and is the start
	// circle: the clicks have to span a measurable bend (else they do not define the pipe - typically clicks along the top
	// instead of across it), and the refined start radius must still match that observed arc (checked below).
	const clickedArc = await estimateClickedArc(queryPoints, axis0, u, v, sCentroid, surfacePoints, surf2D,
		clamp(seedAxialSpread * opts.initRoiAxialSeedSpreadFactor, opts.initRoiAxialMinM, opts.initRoiAxialMaxM), opts);
	if(!clickedArc) return {ok: false, reason: "insufficient_candidates", detail: "clicked_arc_empty"};
	if(!(clickedArc.sagittaToNoise >= opts.initMinSagittaToNoise)){
		return {ok: false, reason: "unstable_initialization", detail: "seed_points_not_across_pipe", clickedArc};
	}

	// The start circle is that observed arc, not the circle through the clicks.
	const start = {cx: clickedArc.cx, cy: clickedArc.cy, r: clickedArc.r};

	// Honest visible arc span of the seed points, measured around the FITTED
	// CIRCLE CENTRE (not their own centroid - a clustered arc subtends a huge
	// angle from its own centroid and would read as near-360°). This is the
	// degeneracy guard (spec §14) AND the radius-identifiability signal
	// carried through tracking (spec §8). While we're here, the MIDDLE of
	// that arc (opposite the largest gap) becomes the SEED ANCHOR (spec:
	// "Brug brugerens surface seeds som soft anchor") - the user's own clicks
	// tell the tracker which contiguous surface to prefer from step one.
	const seedAngs = surf2D.map((p) => Math.atan2(p.y - start.cy, p.x - start.cx)).sort((a, b) => a - b);
	let maxGap = (seedAngs[0] + Math.PI * 2) - seedAngs[seedAngs.length - 1];
	let gapStart = seedAngs[seedAngs.length - 1];
	for(let i = 1; i < seedAngs.length; i++){
		const g = seedAngs[i] - seedAngs[i - 1];
		if(g > maxGap){ maxGap = g; gapStart = seedAngs[i - 1]; }
	}
	const seedArcDeg = (Math.PI * 2 - maxGap) * 180 / Math.PI;
	if(seedArcDeg < opts.initSeedMinArcDeg){
		return {ok: false, reason: "unstable_initialization", detail: "seed_points_near_collinear", seedArcDeg};
	}
	const seedArcCenterAngle = gapStart + maxGap / 2 + Math.PI; // opposite the gap's middle
	// a genuine 3D world-space unit vector (u/v ARE 3D basis vectors here),
	// re-projectable into any later cross-section plane the same way a
	// per-step anchor is (see fitLocalSection)
	const anchorDirWorldInit = u.clone().multiplyScalar(Math.cos(seedArcCenterAngle)).addScaledVector(v, Math.sin(seedArcCenterAngle));

	const r0 = clamp(start.r, opts.initRadiusMinM, opts.initRadiusMaxM);
	const c0 = sCentroid.clone().addScaledVector(u, start.cx).addScaledVector(v, start.cy);
	const station0 = c0.clone().addScaledVector(axis0, dA.clone().add(dB).multiplyScalar(0.5).sub(c0).dot(axis0));
	const roiAxial = clamp(
		Math.max(seedAxialSpread * opts.initRoiAxialSeedSpreadFactor, r0 * opts.initRoiAxialRadiusFactor),
		opts.initRoiAxialMinM, opts.initRoiAxialMaxM);
	const roiRadial = r0 * opts.initRoiRadialFactor;
	const mid = dA.clone().add(dB).multiplyScalar(0.5);

	const roiPoints = await queryPoints(mid, axis0, roiRadial, roiAxial);
	if(roiPoints.length < opts.initMinInliers) return {ok: false, reason: "insufficient_candidates", pointCount: roiPoints.length};

	// The annulus is centred on the SEED-CIRCLE axis (c0, through the pipe centre), not on the line through the direction
	// clicks: those are picked on the pipe SURFACE, so an annulus around them would select a lopsided band of the surface
	// (and clutter) around a line that lies on the pipe, which biased the start radius strongly low on partial arcs.
	// ... and limited to the part of the circumference the user's clicks show (their arc + a margin each side): on a
	// partially buried pipe the rest of the annulus is soil / clutter, which would otherwise dominate the start fit.
	const seedHalfWindow = Math.min(Math.PI, (seedArcDeg / 2 + opts.initSeedArcMarginDeg) * Math.PI / 180);
	const candidates = roiPoints.filter((p) => {
		const d = distanceToAxis(p, c0, axis0);
		if(d < r0 * opts.initAnnulusInner || d > r0 * opts.initAnnulusOuter) return false;
		if(seedHalfWindow >= Math.PI) return true;
		const q = p.clone().sub(c0);
		let da = Math.atan2(q.dot(v), q.dot(u)) - seedArcCenterAngle;
		da = Math.atan2(Math.sin(da), Math.cos(da));
		return Math.abs(da) <= seedHalfWindow;
	});
	if(candidates.length < opts.initMinInliers) return {ok: false, reason: "insufficient_candidates", pointCount: candidates.length};

	const refined = refineCylinderIRLS(candidates, station0, axis0, r0, {axisDirReference: axis0});
	if(!refined || !(refined.radius > 0)) return {ok: false, reason: "fit_did_not_converge"};

	// Re-estimate the radius with a low-bias Taubin fit on the candidates
	// projected ⟂ the refined axis (spec §9). refineCylinderIRLS alternates
	// Kåsa circle fits, which under-estimate the radius of a partial arc under
	// heavy noise by tens of percent - directly biasing the FIXED radius the
	// whole track then runs on. Take the radius from a robust (Tukey-reweighted) Taubin, so soil or a second surface inside
	// the start window cannot pull it. Fall back to the refined radius if Taubin is degenerate.
	// The refined axis direction is only kept if it explains the points better than the clicked direction: on a narrow,
	// noisy arc the cylinder fit can tilt the axis (real data: 1.7° on HV Vand 1), and the cross-section seen along a wrong
	// axis is smeared into a smaller, wrong circle. Never accept a worse fit than the starting one.
	{
		const fitAlong = (dir) => {
			const rb = orthonormalBasis(dir);
			const proj = candidates.map((p) => {
				const d = p.clone().sub(refined.axisPoint);
				return {x: d.dot(rb.u), y: d.dot(rb.v)};
			});
			const robust = robustTaubin(proj, angularSectorPriorWeights(proj), opts.sigmaSurfaceFloorM);
			if(!robust || !(robust.circle.r >= opts.initRadiusMinM && robust.circle.r <= opts.initRadiusMaxM)) return null;
			return {dir, rb, circle: robust.circle, sigma: robust.sigma};
		};
		const alongRefined = fitAlong(refined.axisDir);
		const alongClicked = fitAlong(axis0.clone());
		const best = alongRefined && alongClicked ? (alongClicked.sigma < alongRefined.sigma ? alongClicked : alongRefined) : (alongRefined || alongClicked);
		if(best){
			refined.radius = best.circle.r;
			refined.axisDir = best.dir;
			refined.axisPoint = refined.axisPoint.clone().addScaledVector(best.rb.u, best.circle.cx).addScaledVector(best.rb.v, best.circle.cy);
		}
	}
	if(refined.radius < opts.initRadiusMinM * 0.5 || refined.radius > opts.initRadiusMaxM * 1.5){
		return {ok: false, reason: "radius_out_of_range", radius: refined.radius};
	}

	// σ_surface from a first pass, then the real inlier band from it
	// first band from the candidates' OWN radial spread (robust: 1.4826·median |dist − R|), not a fixed share of R: a doubled
	// or thick surface (two scans, an insulation layer) sits wholly outside a narrow fixed band around the correct radius
	const sigma0 = (() => {
		// only residuals within 0.35R: clutter elsewhere in the annulus must not widen the band
		const res = candidates.map((p) => Math.abs(distanceToAxis(p, refined.axisPoint, refined.axisDir) - refined.radius))
			.filter((d) => d <= 0.35 * refined.radius);
		if(res.length < opts.initMinInliers) return refined.radius * 0.06;
		return clamp(1.4826 * median(res), opts.sigmaSurfaceFloorM, opts.sigmaSurfaceCeilRadiusFactor * refined.radius);
	})();
	let band = bandFromSigma(Math.max(sigma0, refined.radius * 0.06), refined.radius, opts);
	let inliers = candidates.filter((p) => Math.abs(distanceToAxis(p, refined.axisPoint, refined.axisDir) - refined.radius) <= band);
	if(inliers.length < opts.initMinInliers) return {ok: false, reason: "insufficient_inliers", pointCount: inliers.length};

	const initBasis = orthonormalBasis(refined.axisDir);
	const projFor = (pts) => pts.map((p) => {
		const d = p.clone().sub(refined.axisPoint);
		return {x: d.dot(initBasis.u), y: d.dot(initBasis.v)};
	});
	const initAnchorAngle = (() => {
		const refDir = anchorDirWorldInit.clone().projectOnPlane(refined.axisDir);
		return refDir.lengthSq() > EPS ? Math.atan2(refDir.dot(initBasis.v), refDir.dot(initBasis.u)) : null;
	})();

	let residuals = inliers.map((p) => distanceToAxis(p, refined.axisPoint, refined.axisDir) - refined.radius);
	let prior = angularSectorPriorWeights(projFor(inliers));
	let support = computeSurfaceSupport(projFor(inliers), refined.radius, band, opts, initAnchorAngle);
	let sigmaSurface = robustSigmaSurface(residuals, prior, support.supportedCoverageDeg, opts);
	band = bandFromSigma(sigmaSurface, refined.radius, opts);
	inliers = candidates.filter((p) => Math.abs(distanceToAxis(p, refined.axisPoint, refined.axisDir) - refined.radius) <= band);
	residuals = inliers.map((p) => distanceToAxis(p, refined.axisPoint, refined.axisDir) - refined.radius);
	const rms = Math.sqrt(residuals.reduce((s, x) => s + x * x, 0) / Math.max(residuals.length, 1));
	support = computeSurfaceSupport(projFor(inliers), refined.radius, band, opts, initAnchorAngle);

	if(support.supportedCoverageDeg < opts.initMinCoverageDeg){
		return {ok: false, reason: "unstable_initialization", detail: "insufficient_angular_coverage", coverageDeg: support.supportedCoverageDeg, rawCoverageDeg: support.rawCoverageDeg};
	}
	if(rms / Math.max(sigmaSurface, opts.sigmaSurfaceFloorM) > opts.initResidualToNoiseMax){
		return {ok: false, reason: "unstable_initialization", detail: "residual_far_above_noise", residualToNoise: rms / sigmaSurface};
	}

	// A low residual alone does not prove the model on a partially visible pipe: on a short arc several (centre, radius)
	// pairs fit almost equally well, and a wrong start would then confirm itself along the whole track. Two independent
	// checks with the information that is actually there:
	//  (a) the user's own surface clicks are observations of the surface - the model must pass through them;
	//  (b) the radius must be identifiable: two interleaved halves of the inlier population must give the same radius.
	{
		// (c) the visible arc must bend measurably above the surface noise. The sagitta of the supported arc,
		//     R·(1 − cos(span/2)), is how far the arc deviates from a straight chord; when it is only a few σ the radius (and
		//     with it the centre, which sits R behind the surface) is noise-driven and biased - a convincing but wrong line.
		const spanRad = Math.min(Math.PI * 2, Math.max(0, support.supportedCoverageDeg) * Math.PI / 180);
		const sagitta = refined.radius * (1 - Math.cos(Math.min(Math.PI, spanRad / 2)));
		const sagittaToNoise = sagitta / Math.max(sigmaSurface, opts.sigmaSurfaceFloorM);
		if(!(sagittaToNoise >= opts.initMinSagittaToNoise)){
			return {ok: false, reason: "unstable_initialization", detail: "arc_too_flat_for_noise", sagittaToNoise};
		}
		// (d) the data fit must agree with the arc measured from all points across the clicks (see estimateClickedArc). Not
		//     with the circle through the 3-5 clicks themselves: each click carries the full surface noise, so on a narrow arc
		//     that circle is itself badly wrong (a noisy start then gets refused for a correct fit).
		const arcRadiusDiff = Math.abs(refined.radius - clickedArc.r) / Math.max(refined.radius, clickedArc.r);
		if(!(arcRadiusDiff <= opts.initClickedArcRadiusMaxRel)){
			return {ok: false, reason: "unstable_initialization", detail: "radius_inconsistent_with_clicked_arc", arcRadius: clickedArc.r, radius: refined.radius};
		}
		const seedRes = surfacePoints
			.map((sp) => Math.abs(distanceToAxis(sp, refined.axisPoint, refined.axisDir) - refined.radius))
			.sort((a, b) => a - b);
		const seedMed = seedRes[Math.floor(seedRes.length / 2)];
		const seedTol = Math.max(opts.initSeedResidualSigmaFactor * Math.max(sigmaSurface, opts.sigmaSurfaceFloorM),
			opts.initSeedResidualRadiusFactor * refined.radius);
		if(!(seedMed <= seedTol)){
			return {ok: false, reason: "unstable_initialization", detail: "seed_points_off_surface", seedResidualM: seedMed};
		}
		if(inliers.length >= 2 * opts.initMinInliers){
			const halfA = [], halfB = [];
			for(let i = 0; i < inliers.length; i++) (i % 2 ? halfB : halfA).push(inliers[i]);
			const ra = fitCircleTaubin(projFor(halfA));
			const rb = fitCircleTaubin(projFor(halfB));
			if(ra && rb && Math.abs(ra.r - rb.r) / refined.radius > opts.initRadiusSplitMaxRel){
				return {ok: false, reason: "unstable_initialization", detail: "radius_not_identifiable", radiusA: ra.r, radiusB: rb.r};
			}
		}
	}

	// carry the seed anchor forward re-expressed in the REFINED axis's own
	// plane (still a genuine 3D world direction - fitLocalSection re-projects
	// it fresh every step, so this is just its first, most-accurate expression)
	const anchorDirWorld = initAnchorAngle != null
		? initBasis.u.clone().multiplyScalar(Math.cos(initAnchorAngle)).addScaledVector(initBasis.v, Math.sin(initAnchorAngle))
		: anchorDirWorldInit;

	const station = refined.axisPoint.clone().addScaledVector(refined.axisDir, mid.clone().sub(refined.axisPoint).dot(refined.axisDir));
	return {
		ok: true,
		center: station,
		tangent: refined.axisDir.clone().normalize(),
		radius: refined.radius,
		station,
		sigmaSurface,
		coverageDeg: support.supportedCoverageDeg,
		rawCoverageDeg: support.rawCoverageDeg,
		supportedSectorMask: support.supportedSectorMask,
		anchorDirWorld,
		// honest visible arc, measured from the USER'S OWN surface clicks
		// before any circle-fit bias compounds - carried through tracking as
		// the radius-identifiability signal (see fitLocalSection's visibleArcDeg)
		visibleArcDeg: seedArcDeg,
		relResidual: rms / refined.radius,
		residualToNoise: rms / sigmaSurface,
		inlierCount: inliers.length,
		// APPEARANCE seed: PREFER the user's own surface picks (ground truth -
		// they pointed at the visible pipe), fall back to the init inliers.
		initColors: (surfacePoints && surfacePoints.length && surfacePoints[0] && surfacePoints[0].rgb)
			? surfacePoints.map((p) => p.rgb).filter(Boolean)
			: (inliers.length && inliers[0] && inliers[0].rgb ? inliers.map((p) => p.rgb).filter(Boolean) : null),
		initColorsFromSeeds: !!(surfacePoints && surfacePoints.length && surfacePoints[0] && surfacePoints[0].rgb),
	};
}

// ── ITERATIVE, SELF-CONTROLLING CONSISTENCY REFINEMENT (spec metode 1-2-4-8-14)
// Replaces the old one-pass globalConsistencyRefine. Bounded, deterministic,
// explicit convergence. Nothing here queries the point cloud - it reuses each
// section's already-retained (and stride-subsampled) inliers, so an iteration
// that only refits local data never re-fetches octree data (spec §P).

const _subsample = (arr, cap) => {
	if(!arr || arr.length <= cap) return arr || [];
	const stride = arr.length / cap;
	const out = [];
	for(let k = 0; k < cap; k++) out.push(arr[Math.floor(k * stride)]);
	return out;
};

// Confidence-weighted Laplacian centre-line smoother (spec metode 7 - the
// chosen simple alternative to a spline). Unlike smoothCenterline: each
// vertex's Laplacian target is a CONFIDENCE-weighted mean of its neighbours, a
// low-confidence vertex is pulled HARDER toward that target, and the leash to
// the raw centre is relaxed (`maxDeviation` passed in larger) so a genuinely
// drifted section can actually move back toward where the majority + curvature
// prior put it. Endpoints fixed.
function weightedSmoothCenterline(rawPoints, weights, maxDeviation, opts){
	const n = rawPoints.length;
	if(n <= 2) return rawPoints.map((p) => p.clone());
	let pts = rawPoints.map((p) => p.clone());
	for(let pass = 0; pass < opts.smoothingPasses; pass++){
		const next = pts.map((p) => p.clone());
		for(let i = 1; i < n - 1; i++){
			const wPrev = Math.max(weights[i - 1] ?? 1, 0.05);
			const wNext = Math.max(weights[i + 1] ?? 1, 0.05);
			const wSum = wPrev + wNext;
			const target = pts[i - 1].clone().multiplyScalar(wPrev / wSum).addScaledVector(pts[i + 1], wNext / wSum);
			const lap = target.sub(pts[i]);
			const selfW = clamp(weights[i] ?? 1, 0.1, 1);
			next[i].addScaledVector(lap, opts.smoothingLambda * (1 + (1 - selfW)));
			const off = next[i].clone().sub(rawPoints[i]);
			if(off.length() > maxDeviation) next[i].copy(rawPoints[i].clone().addScaledVector(off.normalize(), maxDeviation));
		}
		pts = next;
	}
	return pts;
}

// Continuous surface-trust weight in [0, surfaceRefineMaxWeight] (spec metode 3).
// Every term is LOCAL evidence about whether the observed-surface arc is
// trustworthy enough to move the section centre; the product means any one weak
// signal keeps the weight small. `balance` = min/max supported-sector occupancy
// (→ 0 for a single dense patch: this is the density-skew [4] guard).
function surfaceTrustWeight({spanDeg, balance, surfaceCount, kappa, centerDisagreeRel}, opts){
	if(!(spanDeg > 0) || surfaceCount < opts.obsMinPointFloor) return 0;
	const wSpan = ramp(spanDeg, opts.surfaceTrustSpanZeroDeg, opts.surfaceTrustSpanFullDeg);
	const wBal = clamp01(balance / opts.surfaceTrustBalanceFull);
	const wN = ramp(surfaceCount, opts.obsMinPointFloor, opts.obsMinPointFloor * 4);
	const wKappa = isFinite(kappa) ? invRamp(kappa, opts.obsKappaGood, opts.obsKappaBad) : 0;
	const wAgree = invRamp(centerDisagreeRel, opts.surfaceTrustAgreeGood, opts.surfaceTrustAgreeBad);
	return clamp(wSpan * wBal * wN * wKappa * wAgree, 0, opts.surfaceRefineMaxWeight);
}

// Re-derive each section's OBSERVATION + CONTINUATION confidence from the
// CURRENT (refined) geometry (spec metode 2: accepted sections are provisional -
// their confidence is revised once a better global model exists). Geometric
// continuation terms are measured against the section's refined neighbours, not
// the tracking-time prediction.
function reviseStationConfidence(stations, R, opts){
	for(let i = 0; i < stations.length; i++){
		const s = stations[i];
		if(s.modelled){ s.state = "modelled"; continue; } // held-hypothesis geometry, not observed evidence
		const prev = stations[i - 1], next = stations[i + 1];
		let bendDeg = s.bendDeg ?? 0;
		let centerJumpRel = s.centerJumpRel ?? 0;
		if(prev && next){
			const a = s.center.clone().sub(prev.center);
			const b = next.center.clone().sub(s.center);
			if(a.lengthSq() > EPS && b.lengthSq() > EPS) bendDeg = angleBetweenDeg(a, b);
			const chord = next.center.clone().sub(prev.center);
			if(chord.lengthSq() > EPS){
				const mid = prev.center.clone().add(next.center).multiplyScalar(0.5);
				centerJumpRel = s.center.clone().sub(mid).projectOnPlane(chord.clone().normalize()).length() / R;
			}
		}
		const radiusDeviationRel = s.impliedRadius != null && s.impliedRadius > 0
			? Math.abs(s.impliedRadius - R) / R : (s.radiusDeviationRel ?? 0);
		s.bendDeg = bendDeg;
		s.centerJumpRel = centerJumpRel;
		s.radiusDeviationRel = radiusDeviationRel;
		// REVISED same-object continuity against the refined neighbours + the
		// carried surface anchor (spec: future/global evidence decides the past).
		// Stored SEPARATELY as revisedSameObjectScore/-Components - the LIVE
		// sameObjectScore/-Components from tracking (measured against the real
		// multi-section arc-capable prediction) are left intact so the reported
		// scalar and its components always agree. `contConfidence` still tracks
		// the revised number.
		if(prev && next){
			let t = next.center.clone().sub(prev.center);
			if(t.lengthSq() < EPS) t = s.tangent.clone();
			t.normalize();
			const hyp = {
				center: prev.center.clone().add(next.center).multiplyScalar(0.5),
				tangent: t,
				sigma: predictionSigma(0, R, opts),
			};
			const rev = sameObjectScore(
				{ok: true, center: s.center, tangent: s.tangent, impliedRadius: s.impliedRadius, supportedSectorMask: s.supportedSectorMask},
				hyp, R, prev.anchorDirWorld || s.anchorDirWorld || null, opts, prev.center);
			s.revisedSameObjectScore = rev.score;
			s.revisedSameObjectComponents = {latTerm: rev.latTerm, tangTerm: rev.tangTerm, radTerm: rev.radTerm, surfTerm: rev.surfTerm, curvTerm: rev.curvTerm,
				lat: rev.lat == null ? null : +rev.lat.toFixed(4), tangDeg: rev.tangDeg, radRel: rev.radRel, curvDeg: rev.curvDeg, surfGapDeg: rev.surfGapDeg};
			if(s.sameObjectScore == null){ s.sameObjectScore = rev.score; s.sameObjectComponents = s.revisedSameObjectComponents; }
			s.contConfidence = rev.score;
		}else{
			// endpoint section (only one neighbour) - a minimal geometric check:
			// low bend + implied radius near global = plausibly still the pipe
			s.contConfidence = geomMean([
				invRamp(bendDeg, opts.sosTangGoodDeg, opts.sosTangBadDeg),
				invRamp(radiusDeviationRel, opts.sosRadiusGoodRel, opts.sosRadiusBadRel),
			]);
			if(s.sameObjectScore == null) s.sameObjectScore = s.contConfidence;
		}
		s.obsConfidence = observationConfidence({
			coverageDeg: s.coverageDeg ?? 0,
			residualToNoise: s.residualToNoise ?? 1,
			kappa: s.kappa ?? opts.obsKappaGood,
			pointCount: s.pointCount ?? opts.obsMinPointFloor,
			axialSupport: s.axialSupport ?? 0.5,
		}, opts);
		s.confidence = geomMean([s.obsConfidence, s.contConfidence]);
	}
}

// One-time bidirectional consistency sweep (spec metode 5, cheap version - NO
// second octree pass). At each interior station, fit its centre independently
// from a frame propagated from the LOW-index side and from the HIGH-index side
// (its two tracking directions), and report how far the two independent centres
// disagree. Largest where a section drifted / where the two directions would
// have chosen differently - a path-dependency signal the single greedy pass
// cannot see. Returns metres-per-station (null where not computable).
function bidirectionalDisagreement(stations, R, sigma, opts){
	const n = stations.length;
	const out = new Array(n).fill(null);
	if(n < 5) return out;
	const cap = opts.refinementInlierCap ?? 500;
	const lossScale = Math.max(opts.robustLossSigmaFactor * sigma, opts.bandMinAbsoluteM);
	const fitFrom = (i, neighbourIdx) => {
		const s = stations[i];
		const inl = _subsample(s.inliers, cap);
		if(!inl || inl.length < opts.obsMinPointFloor) return null;
		const ref = stations[Math.max(0, Math.min(n - 1, neighbourIdx))];
		let tang = s.center.clone().sub(ref.center);
		if(tang.lengthSq() < EPS) tang = s.tangent.clone();
		tang.normalize();
		const {u, v} = orthonormalBasis(tang);
		const proj = projectXY(inl, s.center, u, v);
		const f = fitFixedRadiusCenterXY(proj.xs, proj.ys, R, {x: 0, y: 0}, {robustScale: lossScale, priorWeights: angularSectorPriorWeightsXY(proj.xs, proj.ys)});
		if(!f) return null;
		return s.center.clone().addScaledVector(u, f.cx).addScaledVector(v, f.cy);
	};
	for(let i = 1; i < n - 1; i++){
		if(!stations[i].inliers) continue;
		const cLow = fitFrom(i, i - 1);
		const cHigh = fitFrom(i, i + 1);
		if(cLow && cHigh) out[i] = cLow.distanceTo(cHigh);
	}
	return out;
}

function iterativeConsistencyRefine(rawStations, radius, sigmaSurface, opts, fbDisagreement){
	const maxPasses = opts.consistencyMaxPasses ?? 3;
	if(!maxPasses || rawStations.length < 4){
		return {rawStations, radius, sigmaSurface, iterations: 0, converged: true, degraded: false, initialCost: null, finalCost: null, passMs: [], passStats: []};
	}
	let R = radius, sigma = sigmaSurface;
	const cap = opts.refinementInlierCap ?? 500;
	const stations = rawStations.map((s) => ({...s, center: s.center.clone(), tangent: s.tangent.clone()}));
	const subInliers = stations.map((s) => _subsample(s.inliers, cap));
	const now = () => (typeof performance !== "undefined" && performance.now ? performance.now() : Date.now());
	const passMs = [];
	const passStats = [];
	let converged = false, pass = 0;
	// SHAPE cost (curvature + radius-inconsistency + fwd/bwd disagreement) of a station set. Each pass is judged against the
	// BEST state seen so far (starting with the input): an improvement is kept, an insignificant change converges, and a
	// DEGRADATION (or a non-finite cost) is never accepted - the best state is restored and the loop stops.
	const shapeCostOf = (stns, Rr) => {
		let c = 0, cn = 0;
		for(let i = 1; i < stns.length - 1; i++){
			const a = stns[i].center.clone().sub(stns[i - 1].center);
			const b = stns[i + 1].center.clone().sub(stns[i].center);
			if(a.lengthSq() > EPS && b.lengthSq() > EPS){ c += angleBetweenDeg(a, b); cn++; }
		}
		return (cn ? c / cn : 0)
			+ 100 * (mean(stns.map((s) => Math.abs((s.impliedRadius ?? Rr) - Rr) / Rr).filter(isFinite)) ?? 0)
			+ 100 * (mean(stns.map((s) => s.fbDisagreementRel).filter((x) => x != null && isFinite(x))) ?? 0);
	};
	const snapshot = () => ({stations: stations.map((s) => ({...s, center: s.center.clone(), tangent: s.tangent.clone()})), R, sigma});
	// The raw tracked stations are not a comparable solution (each was fitted in its own drifting local frame, masks and
	// implied radii are not yet consistent), so the FIRST pass establishes the refined reference; every later pass is
	// judged against the best refined state.
	let best = null;
	const initialCost = shapeCostOf(stations, R);
	let firstPassCost = null;
	let degraded = false;

	for(pass = 0; pass < maxPasses; pass++){
		const t0 = now();
		const prevMasks = stations.map((s) => (s.supportedSectorMask ? s.supportedSectorMask.slice() : null));
		// 1. regularise into a smooth global centre-line; each section is then
		//    re-fitted against a frame implied by its REGULARISED neighbours.
		//    Weighted by revised confidence AND fwd/bwd disagreement so a
		//    drifted / path-dependent section pulls the smoother less and is
		//    itself pulled harder toward the majority.
		const centres = stations.map((s) => s.center.clone());
		const weights = stations.map((s, i) => {
			// MODELLED gap geometry is the held-hypothesis best estimate - trust
			// it fully so it is not re-smoothed toward the far chord endpoints.
			if(s.modelled) return 1;
			let w = s.confidence == null ? 1 : clamp(s.confidence, 0.15, 1);
			const fb = fbDisagreement && fbDisagreement[i] != null ? fbDisagreement[i] / R : null;
			if(fb != null) w *= 0.15 + 0.85 * invRamp(fb, opts.fbDisagreeGoodRel, opts.fbDisagreeBadRel);
			return w;
		});
		const smoothed = weightedSmoothCenterline(centres, weights, R * opts.consistencyLeashFactor, opts);

		const band = bandFromSigma(sigma, R, opts);
		const lossScale = Math.max(opts.robustLossSigmaFactor * sigma, opts.bandMinAbsoluteM);
		const impliedObs = []; // {r, w}
		const allResiduals = [];
		let maxMoveRel = 0, sumMoveRel = 0, movedCount = 0;

		for(let i = 0; i < stations.length; i++){
			const s = stations[i];
			const inl = subInliers[i];
			if(!inl || inl.length < opts.obsMinPointFloor) continue;

			const prevC = smoothed[Math.max(0, i - 1)];
			const nextC = smoothed[Math.min(stations.length - 1, i + 1)];
			let tang = nextC.clone().sub(prevC);
			if(tang.lengthSq() < EPS) tang = s.tangent.clone();
			tang.normalize();
			const {u, v} = orthonormalBasis(tang);
			const smoothedCenter = smoothed[i];
			const projFrom = (origin) => projectXY(inl, origin, u, v);

			// (a) angularly-balanced robust fixed-R fit
			const p0 = projFrom(smoothedCenter);
			const fullFit = fitFixedRadiusCenterXY(p0.xs, p0.ys, R, {x: 0, y: 0}, {robustScale: lossScale, priorWeights: angularSectorPriorWeightsXY(p0.xs, p0.ys)});
			const centerFull = fullFit
				? smoothedCenter.clone().addScaledVector(u, fullFit.cx).addScaledVector(v, fullFit.cy)
				: smoothedCenter.clone();
			const kappa = fullFit ? fullFit.kappa : Infinity;

			// (b) recompute observed surface support AT the corrected centre
			//     (limitation #4: the mask is a function of the corrected
			//     geometry, and this recompute is the check that stops a wrong
			//     arc self-reinforcing)
			let anchorAngle = null;
			if(s.anchorDirWorld){
				const rd = s.anchorDirWorld.clone().projectOnPlane(tang);
				if(rd.lengthSq() > EPS) anchorAngle = Math.atan2(rd.dot(v), rd.dot(u));
			}
			const projFull = projFrom(centerFull);
			const support = computeSurfaceSupportXY(projFull.xs, projFull.ys, R, band, opts, anchorAngle);
			const sc = support.sectorCount;
			const sectorRad = (Math.PI * 2) / sc;
			const sectorOf = (x, y) => { let a = Math.atan2(y, x); if(a < 0) a += Math.PI * 2; return Math.min(sc - 1, Math.floor(a / sectorRad)); };
			const sectorHits = new Array(sc).fill(0);
			const surfPts = [];
			for(let k = 0; k < projFull.xs.length; k++){ const sec = sectorOf(projFull.xs[k], projFull.ys[k]); if(support.supportedSectorMask[sec]){ sectorHits[sec]++; surfPts.push(inl[k]); } }

			// (c) surface-only fixed-R centre fit
			let centerSurf = centerFull.clone();
			let surfKappa = Infinity;
			if(surfPts.length >= opts.obsMinPointFloor){
				const pS = projectXY(surfPts, centerFull, u, v);
				const sf = fitFixedRadiusCenterXY(pS.xs, pS.ys, R, {x: 0, y: 0}, {robustScale: lossScale, priorWeights: angularSectorPriorWeightsXY(pS.xs, pS.ys)});
				if(sf){ centerSurf = centerFull.clone().addScaledVector(u, sf.cx).addScaledVector(v, sf.cy); surfKappa = sf.kappa; }
			}

			// (d) continuous surface-trust weight
			const nz = sectorHits.filter((c) => c > 0);
			const balance = nz.length ? Math.min(...nz) / Math.max(...nz) : 0;
			const wSurf = surfaceTrustWeight({
				spanDeg: support.supportedCoverageDeg,
				balance,
				surfaceCount: surfPts.length,
				kappa: surfKappa,
				centerDisagreeRel: centerFull.distanceTo(centerSurf) / Math.max(band, EPS),
			}, opts);

			// (e) combine full + surface-supported, then a small pull toward the
			//     regularised global centre-line prior
			let newCenter = centerFull.clone().multiplyScalar(1 - wSurf).addScaledVector(centerSurf, wSurf);
			const wPrior = opts.centerGlobalPriorWeight ?? 0.15;
			newCenter = newCenter.multiplyScalar(1 - wPrior).addScaledVector(smoothedCenter, wPrior);
			// damp: anti-oscillation cap on the per-pass move away from the full fit
			const moveCap = band * (opts.consistencyMoveCapBands ?? 1.5);
			if(newCenter.distanceTo(centerFull) > moveCap){
				newCenter = centerFull.clone().addScaledVector(newCenter.clone().sub(centerFull).normalize(), moveCap);
			}

			const moveRel = newCenter.distanceTo(s.center) / R;
			maxMoveRel = Math.max(maxMoveRel, moveRel);
			sumMoveRel += moveRel; movedCount++;

			// (f) identifiability-weighted implied-radius observation (spec
			//     metode 14): a narrow-arc section has a radius biased tens of
			//     percent - small vote in the global R.
			const rPop = surfPts.length >= opts.obsMinPointFloor ? surfPts : inl;
			const rDist = rPop.map((p) => distanceToAxis(p, newCenter, tang));
			const rObs = weightedMedian(rDist, null);
			const idW = Math.max(0.04,
				ramp(support.supportedCoverageDeg, 60, 170)
				* (isFinite(kappa) ? invRamp(kappa, opts.obsKappaGood, opts.obsKappaBad) : 0.1));
			impliedObs.push({r: rObs, w: idW});
			for(let k = 0; k < rDist.length; k++) allResiduals.push(rDist[k] - R);

			s.center = newCenter;
			s.tangent = tang;
			s.kappa = kappa;
			s.impliedRadius = rObs;
			s.supportedSectorMask = support.supportedSectorMask;
			s.coverageDeg = support.supportedCoverageDeg;
			s.rawCoverageDeg = support.rawCoverageDeg;
			s.surfaceArcSpanDeg = support.arcSpanDeg;
			s.secondArcRel = support.secondArcRel;
			s.secondArcSeparationDeg = support.secondArcSeparationDeg;
			s.surfaceTrustWeight = wSurf;
			s.radiusIdentifiabilityWeight = idW;
		}

		// global R + σ (identifiability-weighted robust median), bounded
		let rConv = true;
		let dRRel = 0;
		const Rbefore = R;
		if(impliedObs.length >= 6){
			// identifiability-weighted target, but the STEP is scaled by the mean identifiability weight (a weighted median is
			// scale-free: a track of only narrow arcs would otherwise hand R its noise-biased value - see pickGlobalRadius)
			const target = weightedMedian(impliedObs.map((o) => o.r), impliedObs.map((o) => o.w));
			const share = clamp(mean(impliedObs.map((o) => o.w)), 0, 1);
			const newR = R + share * (target - R);
			const newSigma = clamp(
				robustSigmaSurface(allResiduals.map((e) => e + R - newR), null, 180, opts),
				opts.sigmaSurfaceFloorM, opts.sigmaSurfaceCeilRadiusFactor * newR);
			dRRel = Math.abs(newR - R) / R;
			rConv = dRRel < (opts.consistencyRConvergeRel ?? 0.004);
			R = newR; sigma = newSigma;
		}

		reviseStationConfidence(stations, R, opts);
		passMs.push(+(now() - t0).toFixed(1));
		let maskChanges = 0;
		for(let i = 0; i < stations.length; i++){
			const a = prevMasks[i], b = stations[i].supportedSectorMask;
			if(!a || !b){ if(a !== b) maskChanges++; continue; }
			for(let k = 0; k < a.length; k++){ if(a[k] !== b[k]){ maskChanges++; break; } }
		}
		if(opts.debug){
			// per-pass convergence trace (development diagnostics only)
			passStats.push({
				maxMoveRel: +maxMoveRel.toFixed(4),
				meanMoveRel: +(sumMoveRel / Math.max(movedCount, 1)).toFixed(4),
				dRRel: +dRRel.toFixed(5),
				maskChanges,
				meanCoverageDeg: +(mean(stations.map((s) => s.coverageDeg).filter((x) => x != null)) ?? 0).toFixed(1),
			});
		}

		// convergence, judged against the best REFINED state so far (see shapeCostOf):
		//  - non-finite cost / radius = numerical instability -> restore the best state, stop (not converged);
		//  - a cost clearly ABOVE the best = degradation -> restore the best state, stop (not converged);
		//  - a clear improvement -> new best; converged when the centres + global R have also gone still;
		//  - an insignificant change (within ±consistencyShapeImproveRel of the best) -> converged, the better of the two kept.
		const cost = shapeCostOf(stations, R);
		const relTol = opts.consistencyShapeImproveRel ?? 0.03;
		const finite = isFinite(cost) && isFinite(R) && R > 0;
		if(best === null){
			if(!finite){ degraded = true; pass++; break; }
			firstPassCost = cost;
			best = {...snapshot(), cost};
			if(rConv && maxMoveRel < (opts.consistencyMoveConvergeRel ?? 0.02)){ converged = true; pass++; break; }
			continue;
		}
		const bestCost = best.cost;
		if(!finite || cost > bestCost * (1 + relTol) + 1e-9){
			degraded = true;
			pass++;
			break;
		}
		const improved = cost < bestCost * (1 - relTol);
		if(cost <= bestCost) best = {...snapshot(), cost};
		if(rConv && (maxMoveRel < (opts.consistencyMoveConvergeRel ?? 0.02) || !improved)){
			converged = true; pass++; break;
		}
	}
	if(best && (degraded || best.cost < shapeCostOf(stations, R))){
		// keep the best valid state, never a worse last iteration
		for(let i = 0; i < stations.length; i++) stations[i] = best.stations[i];
		R = best.R; sigma = best.sigma;
	}
	const finalCost = shapeCostOf(stations, R);

	// ── LONGITUDINAL SURFACE CONTINUITY (spec metode 8) ──────────────────
	// A real pipe surface is a CONNECTED strip in (station, sector) with a
	// radius that holds ≈ R along the whole run; trench backfill that
	// momentarily sits within the radial tolerance is far patchier and its
	// per-sector radius wanders from station to station. Two cheap, non-image-
	// processing filters, applied to the final per-station masks:
	//
	//   check 1 - angular continuity: a supported sector survives only if a ±1
	//             neighbour sector is also supported at an adjacent station.
	//   check 2 - radial coherence: a supported sector's own median radius at
	//             this station must agree (within the band) with that sector's
	//             median radius across its longitudinal neighbourhood; a sector
	//             that is only supported at a couple of isolated stations along
	//             the whole track is dropped as a fragment.
	const sectorRadiusPerStation = stations.map((s) => {
		if(!s.inliers || !s.supportedSectorMask) return null;
		const {u, v} = orthonormalBasis(s.tangent);
		const sc = s.supportedSectorMask.length;
		const sr = (Math.PI * 2) / sc;
		const bins = Array.from({length: sc}, () => []);
		const c = s.center;
		for(const p of s.inliers){
			const dx = p.x - c.x, dy = p.y - c.y, dz = p.z - c.z;
			const du = dx * u.x + dy * u.y + dz * u.z, dv = dx * v.x + dy * v.y + dz * v.z;
			let a = Math.atan2(dv, du); if(a < 0) a += Math.PI * 2;
			bins[Math.min(sc - 1, Math.floor(a / sr))].push(Math.hypot(du, dv));
		}
		return bins.map((b) => (b.length ? median(b) : null));
	});
	const bandNow = bandFromSigma(sigma, R, opts);
	const nStations = stations.length;
	// per sector: at how many stations it is supported (with a measured radius) - counted once, O(n·sectors), instead of
	// re-scanning every station for every (station, sector) pair
	const sectorSupportCount = [];
	const supportedWithRadius = (k, sec) => {
		const rr = sectorRadiusPerStation[k];
		return !!(rr && rr[sec] != null && stations[k].supportedSectorMask && stations[k].supportedSectorMask[sec]);
	};
	for(let k = 0; k < nStations; k++){
		const m = stations[k].supportedSectorMask;
		if(!m) continue;
		for(let sec = 0; sec < m.length; sec++){
			if(supportedWithRadius(k, sec)) sectorSupportCount[sec] = (sectorSupportCount[sec] || 0) + 1;
		}
	}
	const coherent = stations.map((s, i) => {
		const mask = s.supportedSectorMask;
		if(!mask) return null;
		const mine = sectorRadiusPerStation[i];
		return mask.map((v, sec) => {
			if(!v) return false;
			if(!mine || mine[sec] == null) return v;
			// this sector's radius across k-2 .. k+2
			const neigh = [];
			const supportedElsewhere = (sectorSupportCount[sec] || 0) - (supportedWithRadius(i, sec) ? 1 : 0);
			for(let k = Math.max(0, i - 2); k <= Math.min(nStations - 1, i + 2); k++){
				if(k !== i && supportedWithRadius(k, sec)) neigh.push(sectorRadiusPerStation[k][sec]);
			}
			// a sector supported at essentially only this station (and maybe one
			// neighbour) along the whole track is a fragment, not a surface
			if(supportedElsewhere < Math.min(2, nStations - 2)) return false;
			if(neigh.length === 0) return true;
			return Math.abs(mine[sec] - median(neigh)) <= bandNow;
		});
	});
	const continuous = longitudinalContinuityFilter(coherent.map((m) => m || null));
	for(let i = 0; i < stations.length; i++){
		const s = stations[i];
		if(continuous[i]){
			s.supportedSectorMask = continuous[i];
			const cov = maskCoverageDeg(continuous[i]);
			if(cov != null) s.coverageDeg = cov;
		}
		// refresh the FULL-resolution surface population against the final geometry + filtered mask (the loop used a
		// subsampled set only to fit). Only the development diagnostics (classification / inlier export) read it.
		if(opts.debug && s.inliers && s.supportedSectorMask){
			const {u, v} = orthonormalBasis(s.tangent);
			const sc = s.supportedSectorMask.length;
			const sr = (Math.PI * 2) / sc;
			const c = s.center;
			s.surfacePoints = s.inliers.filter((p) => {
				const dx = p.x - c.x, dy = p.y - c.y, dz = p.z - c.z;
				const du = dx * u.x + dy * u.y + dz * u.z, dv = dx * v.x + dy * v.y + dz * v.z;
				let a = Math.atan2(dv, du); if(a < 0) a += Math.PI * 2;
				return s.supportedSectorMask[Math.min(sc - 1, Math.floor(a / sr))];
			});
			s.surfacePointCount = s.surfacePoints.length;
		}
	}
	reviseStationConfidence(stations, R, opts);

	return {rawStations: stations, radius: R, sigmaSurface: sigma, iterations: pass, converged, degraded, initialCost, firstPassCost, finalCost, passMs, passStats};
}

// Global pipe-consistency objective (spec metode 6): a VECTOR of independently
// meaningful components, never collapsed into one opaque confidence number.
function consistencyVector(stations, centerline, R, opts){
	const acc = stations.filter((s) => !s.coasted);
	const rtn = acc.map((s) => s.residualToNoise).filter((x) => x != null && isFinite(x));
	const cov = acc.map((s) => s.coverageDeg).filter((x) => x != null);
	const impl = acc.map((s) => s.impliedRadius).filter((x) => x != null && x > 0);
	const fb = acc.map((s) => s.fbDisagreementRel).filter((x) => x != null && isFinite(x));
	let curv = 0, cn = 0;
	for(let i = 1; i < centerline.length - 1; i++){
		const a = centerline[i].clone().sub(centerline[i - 1]);
		const b = centerline[i + 1].clone().sub(centerline[i]);
		if(a.lengthSq() > EPS && b.lengthSq() > EPS){ curv += angleBetweenDeg(a, b) ** 2; cn++; }
	}
	const radiusInconsistency = impl.length > 1
		? Math.sqrt(impl.reduce((s, r) => s + (r - R) * (r - R), 0) / impl.length) / R : 0;
	return {
		meanResidualToNoise: mean(rtn) ?? 0,
		meanSupportedCoverageDeg: mean(cov) ?? 0,
		curvatureRmsDeg: cn ? Math.sqrt(curv / cn) : 0,
		radiusInconsistency,
		meanFbDisagreementRel: mean(fb) ?? 0,
		unsupportedFraction: stations.length ? stations.filter((s) => s.coasted).length / stations.length : 0,
		structuralJumpCount: acc.filter((s) => (s.centerJumpRel ?? 0) > opts.structuralCenterJumpRel || (s.bendDeg ?? 0) > opts.structuralBendDeg).length,
	};
}

// Post-refine endpoint re-evaluation (spec metode 12): with the global model
// known, re-judge each end using the enriched signals (post-filter supported
// coverage, fwd/bwd disagreement, ambiguity, ROI point count). Classifies the
// end and trims a weak tail. `endType`: observed | occluded | data_end |
// ambiguous. Replaces the aggressive pre-refine end-trim (a minimal "drop
// trailing coasted" still runs before the loop so it is not fed a coasted tail).
function evaluateEndpoints(stations, R, opts){
	const n = stations.length;
	if(n <= 3) return {stations, ends: {backward: {type: "observed", trimmed: 0}, forward: {type: "observed", trimmed: 0}}};
	const classifyEnd = (idx, nb) => {
		const s = stations[idx];
		const ambiguous = (s.secondArcRel ?? 0) > (opts.ambiguitySecondArcRel ?? 0.5)
			&& (s.secondArcSeparationDeg ?? 0) > (opts.ambiguityMinSeparationDeg ?? 40);
		if(ambiguous) return "ambiguous";
		if(s.coasted === true) return "data_end";
		if(s.roiPointCount != null && s.roiPointCount < opts.obsMinPointFloor * 2) return "data_end";
		const neigh = stations[nb];
		if(neigh && (s.coverageDeg ?? 0) < (neigh.coverageDeg ?? (s.coverageDeg ?? 0)) * 0.6) return "occluded";
		return "observed";
	};
	let lo = 0, hi = n - 1;
	const ends = {};
	for(const side of ["backward", "forward"]){
		let trimmed = 0;
		while(hi - lo > 2){
			const idx = side === "backward" ? lo : hi;
			const nb = side === "backward" ? lo + 1 : hi - 1;
			const s = stations[idx];
			const type = classifyEnd(idx, nb);
			const weakSupport = s.axialSupport != null && s.axialSupport < opts.endTrimMinAxialSupport;
			const weakCont = s.contConfidence != null && s.contConfidence < opts.endTrimMinContinuation;
			const highFb = s.fbDisagreementRel != null && s.fbDisagreementRel > (opts.fbDisagreeBadRel ?? 0.5);
			const collapsed = type !== "observed";
			if((collapsed && (weakSupport || weakCont || highFb)) || (weakSupport && weakCont)){
				if(side === "backward") lo++; else hi--;
				trimmed++;
				continue;
			}
			ends[side] = {type, trimmed};
			break;
		}
		if(!ends[side]) ends[side] = {type: "observed", trimmed};
	}
	return {stations: stations.slice(lo, hi + 1), ends};
}

// Retrospective revision (spec §6: future / global evidence decides the past -
// accepted sections are NOT irreversible truth). After refinement, walk each
// direction's run OUTWARD from the initial model:
//   • a CHANGE-POINT candidate is a section whose CURVATURE- and STEP-aware
//     recheck score drops below interiorDropSos AND whose own supported arc has
//     collapsed (< interiorDropCoverageDeg). A SINGLE such section on a gently
//     curving or sparsely-sampled stretch is NOT an object switch - the recheck
//     predictor is deliberately crude, and a density-driven step-size spike
//     alone can defeat it while every real indicator still says "same pipe".
//   • only a PERSISTENT contiguous run (≥ objSwitchRunLen) of collapsed low-
//     score sections that never recovers a healthy supported arc is truncated
//     (state:"wrong_object", dropped from the authoritative model, returned for
//     transparency). Truncation stops at the first section that recovers a
//     healthy arc - a real switch does not re-wrap a full radius-matched
//     cylinder further on.
//   • every surviving directly-observed section is (re)labelled SUPPORTED or
//     UNCERTAIN from its recheck score, stored as revisedSameObjectScore; the
//     LIVE sameObjectScore/-Components from tracking are left intact.
//   • MODELLED gap sections pass through untouched.
function retrospectiveRevision(rawStations, radius, opts){
	const initIdx = rawStations.findIndex((s) => s.synthetic);
	if(initIdx < 0) return {stations: rawStations, dropped: []};
	const dropped = [];
	const keep = new Array(rawStations.length).fill(true);
	const RUN = Math.max(2, opts.objSwitchRunLen ?? 2);

	const asSection = (s) => ({ok: true, center: s.center, tangent: s.tangent, impliedRadius: s.impliedRadius, supportedSectorMask: s.supportedSectorMask});
	const collapsedAt = (s) => (s.coverageDeg ?? 360) < opts.interiorDropCoverageDeg;

	// Curvature- AND step-aware recheck of station indices[k]: predict it with
	// the SAME robust multi-point predictor the live loop uses (predictAhead over
	// the surviving preceding centres), and scale the prediction sigma to the
	// ACTUAL step. The previous floor-sigma straight 2-point line was defeated by
	// a gentle curve or a density-driven step-size spike even when every
	// geometric term said "same pipe".
	const recheck = (indices, k) => {
		const hIdx = [];
		for(let j = 0; j < k; j++){ const g = indices[j]; if(!rawStations[g].modelled && keep[g]) hIdx.push(g); }
		if(hIdx.length < 2) return null;
		const p1 = rawStations[hIdx[hIdx.length - 1]];
		const hist = hIdx.map((j) => rawStations[j].center.clone());
		let tan0 = hist[hist.length - 1].clone().sub(hist[hist.length - 2]);
		tan0 = tan0.lengthSq() < EPS ? p1.tangent.clone() : tan0.normalize();
		const reach = Math.max(rawStations[indices[k]].center.distanceTo(p1.center), radius * 0.5);
		const pr = predictAhead(hist, tan0, reach, radius, opts);
		const hyp = {center: pr.center, tangent: pr.tangent, sigma: predictionSigma(reach, radius, opts)};
		return sameObjectScore(asSection(rawStations[indices[k]]), hyp, radius, p1.anchorDirWorld || null, opts, p1.center).score;
	};

	const scanRun = (indices) => {
		for(let k = 2; k < indices.length; k++){
			const i = indices[k];
			if(rawStations[i].modelled) continue;
			const sc = recheck(indices, k);
			if(sc == null) continue;
			rawStations[i].revisedSameObjectScore = +sc.toFixed(4);
			rawStations[i].state = sc >= opts.acceptSameObjectScore ? "supported" : "uncertain";
			if(sc >= opts.interiorDropSos || !collapsedAt(rawStations[i])) continue;

			// extend a contiguous run of collapsed low-score sections; stop at the
			// first one that recovers (score ok OR arc no longer collapsed). A
			// MODELLED section inside the run means the live loop coasted this
			// stretch and REACQUIRED the same pipe on its far side (spec §5) -
			// that is proof it is NOT an object switch: abort the trim.
			let end = k, reacquiredAhead = false;
			for(let m = k + 1; m < indices.length; m++){
				const sm = rawStations[indices[m]];
				if(sm.modelled){ reacquiredAhead = true; break; }
				const scm = recheck(indices, m);
				if(scm == null) break;
				sm.revisedSameObjectScore = +scm.toFixed(4);
				if(scm < opts.interiorDropSos && collapsedAt(sm)){ end = m; }
				else break;
			}
			if(reacquiredAhead) continue;   // coasted past a fitting, not a switch
			if(end - k + 1 < RUN) continue; // transient weak patch, not a switch

			for(let m = k; m <= end; m++){
				keep[indices[m]] = false;
				rawStations[indices[m]].state = "wrong_object";
				rawStations[indices[m]].droppedReason = "object_switch";
				dropped.push(rawStations[indices[m]]);
			}
			k = end; // keep scanning the surviving tail for a later independent switch
		}
	};

	const fwdIdx = []; for(let i = initIdx; i < rawStations.length; i++) fwdIdx.push(i);
	const bwdIdx = []; for(let i = initIdx; i >= 0; i--) bwdIdx.push(i);
	scanRun(fwdIdx);
	scanRun(bwdIdx);

	return {stations: rawStations.filter((_, i) => keep[i]), dropped};
}

// ── entry point ─────────────────────────────────────────────────────────
export async function trackPipe({queryPoints: rawQueryPoints, initialization, options = {}}){
	const opts = {...TRACKING_DEFAULTS, ...options};

	// Timing af ROI-forespørgslerne (wall-tid, inkl. ventetid på node-indlæsning). Adapteren (pipeRoiAdapter) leverer desuden opdelingen
	// load-ventetid / indeks-opbygning / scanning via queryPoints.stats; diffen for netop denne kørsel lægges i timings (quality.timings).
	const perfNow = () => (typeof performance !== "undefined" && performance.now ? performance.now() : Date.now());
	const queryTimer = {ms: 0, calls: 0};
	const queryStats0 = rawQueryPoints && rawQueryPoints.stats ? {...rawQueryPoints.stats} : null;
	const queryPoints = async (...args) => {
		const tq = perfNow();
		try{
			return await rawQueryPoints(...args);
		}finally{
			queryTimer.ms += perfNow() - tq;
			queryTimer.calls++;
		}
	};
	// prefetch (kun cache-opvarmning, se pipeRoiAdapter) tælles ikke som forespørgsel og påvirker aldrig resultatet
	if(rawQueryPoints && typeof rawQueryPoints.prefetch === "function") queryPoints.prefetch = rawQueryPoints.prefetch;

	// lightweight phase profiler (spec §O/§R: profile before/after)
	const now = () => (typeof performance !== "undefined" && performance.now ? performance.now() : Date.now());
	const t0 = now();
	const timings = {};
	const mark = (name, since) => { timings[name] = +(now() - since).toFixed(1); };

	let model;
	if(opts.onStage) opts.onStage(t("Initialiserer…"));
	if(initialization && initialization.center && initialization.tangent && initialization.radius){
		model = {
			center: initialization.center.clone(),
			tangent: initialization.tangent.clone().normalize(),
			radius: initialization.radius,
			sigmaSurface: initialization.sigmaSurface || null,
			coverageDeg: null, relResidual: null, inlierCount: null,
			supportedSectorMask: initialization.supportedSectorMask || null,
			anchorDirWorld: initialization.anchorDirWorld || null,
		};
	}else{
		if(opts.shouldCancel && opts.shouldCancel()) return {status: "cancelled"};
		const tInit = now();
		const est = await estimateInitialModel(queryPoints, initialization.directionPoints, initialization.surfacePoints, opts);
		mark("initMs", tInit);
		if(!est.ok) return {status: "unstable_initialization", initialization: est};
		model = est;
	}
	if(opts.shouldCancel && opts.shouldCancel()) return {status: "cancelled"};

	// the top line needs a non-vertical axis (see computeTopLine / maxAxisInclinationDeg)
	if(axisInclinationDeg(model.tangent) > opts.maxAxisInclinationDeg) return {status: "too_steep"};

	// Forward and backward are tracked SEQUENTIALLY, not in parallel: an async
	// queryPoints (the octree adapter) triggers node loads, and two directions
	// competing for the loader gate at once only slows both down. Sequential
	// also keeps the cancellation check responsive.
	const tFwd = now();
	const fwd = await trackDirection(queryPoints, model, +1, opts);
	mark("forwardMs", tFwd);
	if(opts.onYield) await opts.onYield(true);
	if(opts.shouldCancel && opts.shouldCancel()) return {status: "cancelled"};
	const tBwd = now();
	const bwd = await trackDirection(queryPoints, model, -1, opts);
	mark("backwardMs", tBwd);
	if(opts.onYield) await opts.onYield(true);
	if(opts.shouldCancel && opts.shouldCancel()) return {status: "cancelled"};

	const acceptedBwd = fwd_bwdAccepted(bwd.stations);
	const acceptedFwd = fwd_bwdAccepted(fwd.stations);
	let rawStations = [
		...acceptedBwd.slice().reverse(),
		{synthetic: true, center: model.center.clone(), tangent: model.tangent.clone(), radius: model.radius, coasted: false,
			confidence: 1, obsConfidence: 1, contConfidence: 1, impliedRadius: model.radius, radiusDeviationRel: 0,
			coverageDeg: model.coverageDeg, rawCoverageDeg: model.rawCoverageDeg ?? null, relResidual: model.relResidual, residualToNoise: model.residualToNoise,
			sectionSigma: model.sigmaSurface, pointCount: model.inlierCount, surfacePointCount: model.inlierCount, radialRMS: null, kappa: null,
			bendDeg: 0, axialSupport: 1, radiusUncertainty: null, inliers: null, surfacePoints: null,
			supportedSectorMask: model.supportedSectorMask || null, anchorDirWorld: model.anchorDirWorld || null},
		...acceptedFwd,
	];

	let radius = pickGlobalRadius(rawStations, model.radius);
	let sigmaSurface = median([model.sigmaSurface || opts.sigmaSurfaceFloorM, fwd.sigmaSurface, bwd.sigmaSurface].filter((x) => x > 0));

	if(opts.onStage) opts.onStage(t("Forfiner model…"));
	if(opts.onYield) await opts.onYield(true); // paint the stage text before the (synchronous) refinement
	const tRefine = now();

	// (1) bidirectional path-dependency signal on the assembled track, BEFORE
	//     the loop, so it can down-weight drifted / path-dependent sections.
	const fbRaw = bidirectionalDisagreement(rawStations, radius, sigmaSurface, opts);
	for(let i = 0; i < rawStations.length; i++){
		rawStations[i].fbDisagreementM = fbRaw[i];
		rawStations[i].fbDisagreementRel = fbRaw[i] != null ? fbRaw[i] / radius : null;
	}

	// (2) iterative consistency refinement (keeps the best valid state, see iterativeConsistencyRefine)
	const refined = iterativeConsistencyRefine(rawStations, radius, sigmaSurface, opts, fbRaw);
	rawStations = refined.rawStations;
	radius = refined.radius;
	sigmaSurface = refined.sigmaSurface;
	if(opts.shouldCancel && opts.shouldCancel()) return {status: "cancelled"};

	// (3) retrospective revision: drop a run's tail shown to be another object (modelled gap geometry passes through)
	const objTrim = retrospectiveRevision(rawStations, radius, opts);
	const droppedStations = objTrim.dropped;
	rawStations = objTrim.stations;

	// (4) post-refine endpoint re-evaluation
	const endEval = evaluateEndpoints(rawStations, radius, opts);
	rawStations = endEval.stations;
	mark("refineMs", tRefine);

	// (5) the two product lines (centre-line + top-line, one error budget - see buildPipeLines)
	const lines = buildPipeLines(rawStations.map((s) => s.center), radius, sigmaSurface, opts);
	const followedLengthForward = polylineLen(acceptedFwd.map((s) => s.center), model.center);
	const followedLengthBackward = polylineLen(acceptedBwd.map((s) => s.center), model.center);
	const acceptedAll = rawStations.filter((s) => !s.synthetic);
	const switchM = [fwd.switchStationM, bwd.switchStationM].filter((x) => x != null);

	// Minimal status for the caller (success / failure handling) and for the tests' invariants. No report data.
	const quality = {
		followedLengthForward,
		followedLengthBackward,
		followedLengthTotal: followedLengthForward + followedLengthBackward,
		centerlineLength: lines ? polylineLen(lines.centerline) : 0,
		acceptedSectionCount: acceptedAll.length,
		coastedSectionCount: [...fwd.stations, ...bwd.stations].filter((s) => s.coasted).length,
		droppedSectionCount: droppedStations.length,
		modelledSectionCount: rawStations.filter((s) => s.modelled).length,
		reacquisitionCount: (fwd.reacquiredCount || 0) + (bwd.reacquiredCount || 0),
		objectSwitchStationM: switchM.length ? +Math.max(...switchM).toFixed(2) : null,
		radius,
		sigmaSurface,
		lineTolerance: lines ? lines.tolerance : null,
		refineIterations: refined.iterations,
		refineConverged: refined.converged,
		endTypes: endEval.ends,
		stopReasonForward: fwd.stopReason,
		stopReasonBackward: bwd.stopReason,
	};
	mark("totalMs", t0);

	const result = {
		status: lines && acceptedAll.length > 0 ? "ok" : "insufficient_data",
		radius,
		sigmaSurface,
		centerline: lines ? lines.centerline : [],
		topLine: lines ? lines.topLine : [],
		quality,
	};
	if(!opts.debug) return result;

	// ── development diagnostics (opts.debug only; never used by the tool, never stored) ──
	timings.queryMs = +queryTimer.ms.toFixed(1);
	timings.queryCalls = queryTimer.calls;
	if(queryStats0){
		const s1 = rawQueryPoints.stats;
		const d = (k) => +((s1[k] || 0) - (queryStats0[k] || 0)).toFixed(1);
		timings.queryLoadWaitMs = d("loadWaitMs");
		timings.queryBuildMs = d("buildMs");
		timings.queryScanMs = d("scanMs");
		timings.queryNodesLoaded = d("nodesLoadedOnDemand");
		timings.queryCacheMisses = d("cacheMisses");
		timings.queryCacheHits = d("cacheHits");
	}
	timings.refinePassMs = refined.passMs;
	timings.refinePassStats = refined.passStats;
	const modelledSpans = [];
	{
		let acc = 0, spanStart = null;
		for(let i = 0; i < rawStations.length; i++){
			if(i > 0) acc += rawStations[i].center.distanceTo(rawStations[i - 1].center);
			if(rawStations[i].modelled){ if(spanStart == null) spanStart = acc; }
			else if(spanStart != null){ modelledSpans.push({fromM: +spanStart.toFixed(2), toM: +acc.toFixed(2)}); spanStart = null; }
		}
		if(spanStart != null) modelledSpans.push({fromM: +spanStart.toFixed(2), toM: +acc.toFixed(2)});
	}
	const band = bandFromSigma(sigmaSurface, radius, opts);
	const denseCenterline = smoothCenterline(rawStations.map((s) => s.center), radius * opts.smoothingMaxDeviationFactor, opts);
	const appDirs = [fwd.appearance, bwd.appearance].filter((a) => a && a.available);
	const withModel = appDirs.filter((a) => a.model && a.model.ok).sort((x, y) => (y.confidence || 0) - (x.confidence || 0));
	const appModelFinal = withModel.length ? withModel[0].model : null;
	const appConfFinal = withModel.length ? (withModel[0].confidence || 0) : 0;
	const seps = appDirs.map((a) => a.sepBgMedian).filter((x) => x != null);
	const appSepBgFinal = seps.length ? median(seps) : null;
	const appStrengthClass = appModelFinal && appSepBgFinal != null
		? +clamp(appConfFinal * clamp(appSepBgFinal / opts.appSepGoodStrength, 0, 1) * opts.appMaxSurfaceInfluence, 0, opts.appMaxSurfaceInfluence).toFixed(3) : 0;
	const classification = buildSurfaceClassification(
		rawStations.map((s, i) => ({center: denseCenterline[i], tangent: s.tangent, radius, band, supportedSectorMask: s.supportedSectorMask || null})),
		sigmaSurface,
		appModelFinal && appStrengthClass > 0 ? {pointSimilarity: (rgb) => appearancePointSimilarity(appModelFinal, rgb, opts), strength: appStrengthClass} : null,
	);
	const inlierPoints = [];
	for(const s of rawStations){
		const pts = s.surfacePoints || s.inliers;
		if(pts) for(const p of pts){ const c = p.clone(); if(p.rgb) c.rgb = p.rgb; inlierPoints.push(c); }
	}
	const appSecs = acceptedAll.filter((s) => s.appearanceSimilarity != null);
	const appearance = {
		available: appDirs.length > 0,
		modelReady: !!(appModelFinal && appModelFinal.ok),
		modes: appModelFinal && appModelFinal.ok
			? appModelFinal.modes.map((m) => ({cr: +m.cr.toFixed(3), cg: +m.cg.toFixed(3), Y: +m.Y.toFixed(0), weight: +m.w.toFixed(3)})) : [],
		confidence: +appConfFinal.toFixed(3),
		discriminativeStrengthBg: appSepBgFinal == null ? null : +appSepBgFinal.toFixed(3),
		surfaceInfluence: appStrengthClass,
		meanSectionSimilarity: appSecs.length ? +mean(appSecs.map((s) => s.appearanceSimilarity)).toFixed(3) : null,
		meanSectionStrength: appSecs.length ? +mean(appSecs.map((s) => s.appearanceStrength ?? 0)).toFixed(3) : null,
		minSectionAppTerm: appSecs.length ? +Math.min(...appSecs.map((s) => s.appearanceTerm ?? 1)).toFixed(3) : null,
		sectionsUpdatedModel: acceptedAll.filter((s) => s.appearanceModelUpdated).length,
	};
	Object.assign(quality, {
		modelledLengthM: modelledSpans.reduce((a, s) => a + (s.toM - s.fromM), 0),
		meanCoverageDeg: mean(acceptedAll.map((s) => s.coverageDeg).filter((x) => x != null)),
		meanRawCoverageDeg: mean(acceptedAll.map((s) => s.rawCoverageDeg).filter((x) => x != null)),
		minObservationConfidence: acceptedAll.length ? Math.min(...acceptedAll.map((s) => s.obsConfidence ?? 1)) : 1,
		minContinuationConfidence: acceptedAll.length ? Math.min(...acceptedAll.map((s) => s.contConfidence ?? 1)) : 1,
		meanResidualToNoise: mean(acceptedAll.map((s) => s.residualToNoise).filter((x) => x != null)),
		ambiguousStationCount: rawStations.filter((s) => (s.secondArcRel ?? 0) > opts.ambiguitySecondArcRel
			&& (s.secondArcSeparationDeg ?? 0) > opts.ambiguityMinSeparationDeg).length,
		maxSecondArcRel: rawStations.reduce((m, s) => Math.max(m, s.secondArcRel ?? 0), 0),
		fbDisagreementMaxRel: rawStations.reduce((m, s) => Math.max(m, s.fbDisagreementRel ?? 0), 0),
		refineInitialCost: refined.initialCost,
		refineFirstPassCost: refined.firstPassCost,
		refineFinalCost: refined.finalCost,
		refineDegraded: refined.degraded,
		appearanceAvailable: appearance.available,
		appearanceModelReady: appearance.modelReady,
		timings,
	});
	return Object.assign(result, {
		model: {center: model.center.clone(), tangent: model.tangent.clone(), radius: model.radius, sigmaSurface: model.sigmaSurface},
		denseCenterline,
		tangents: polylineVertexTangents(result.centerline),
		sections: acceptedAll,
		rawStations,
		droppedStations,
		modelledSpans,
		classification,
		appearance,
		converged: refined.converged,
		consistency: consistencyVector(rawStations, result.centerline, radius, opts),
		isPipeInlier: (p) => isSurfaceInlier(p, classification, opts.classificationInlierThreshold),
		pipeInlierWeight: (p) => surfaceInlierWeight(p, classification),
		inlierPoints,
		diagnostics: {
			modelledSpans,
			appearance,
			decisionTrace: {forward: fwd.decisionLog || [], backward: bwd.decisionLog || []},
			reacquisition: {forward: fwd.reacquireAttempts || [], backward: bwd.reacquireAttempts || []},
			droppedStations: droppedStations.map((s) => ({
				center: [s.center.x, s.center.y, s.center.z],
				sameObjectScore: s.sameObjectScore ?? null,
				revisedSameObjectScore: s.revisedSameObjectScore ?? null,
				components: s.sameObjectComponents || null,
				coverageDeg: s.coverageDeg ?? null,
				droppedReason: s.droppedReason || "object_switch",
			})),
		},
	});
}

// Global radius from the tracked sections, moved from the start model's radius only as far as the sections can actually
// IDENTIFY a radius: a section's implied radius is weighted by its supported arc (narrow arcs give a strongly biased radius,
// see estimateInitialModel), and the step from `fallback` is scaled by the mean weight. A weighted median alone is scale-free,
// so a track made only of narrow arcs would otherwise replace a good radius by its biased one.
function pickGlobalRadius(rawStations, fallback){
	const obs = rawStations
		.filter((s) => !s.synthetic && s.impliedRadius != null && s.impliedRadius > 0)
		.map((s) => ({r: s.impliedRadius, w: radiusIdentifiability(s.coverageDeg)}));
	if(!obs.length) return fallback;
	const target = weightedMedian(obs.map((o) => o.r), obs.map((o) => o.w));
	const share = clamp(mean(obs.map((o) => o.w)), 0, 1);
	return fallback + share * (target - fallback);
}
// 0 below a 60° supported arc, 1 from 170°: how well a single section's arc pins its radius
function radiusIdentifiability(coverageDeg){
	return coverageDeg == null ? 0.5 : ramp(coverageDeg, 60, 170);
}

function fwd_bwdAccepted(stations){
	// Directly-observed sections up to the last accepted one, PLUS the MODELLED
	// held-hypothesis geometry through any reacquired gap in between (those have
	// `keepInCenterline` - they carry no fitting evidence but the pipe does pass
	// through them, so the centre-line follows the predicted path across the gap
	// instead of a straight chord). The end decision runs later in
	// evaluateEndpoints; only a trailing NON-modelled coasted run is dropped here.
	let lastAccepted = -1;
	for(let i = 0; i < stations.length; i++) if(!stations[i].coasted) lastAccepted = i;
	return stations.slice(0, lastAccepted + 1).filter((s) => !s.coasted || s.keepInCenterline);
}

function polylineLen(points, prepend){
	const seq = prepend ? [prepend.clone(), ...points] : points;
	let L = 0;
	for(let i = 0; i < seq.length - 1; i++) L += seq[i + 1].distanceTo(seq[i]);
	return L;
}
export function polylineLength(points){
	let L = 0;
	for(let i = 0; i < points.length - 1; i++) L += points[i + 1].distanceTo(points[i]);
	return L;
}
